import { useState, useEffect, useRef } from 'react';
import { formatPhone } from '../components/formatPhone';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import ConfirmModal from '../components/ConfirmModal';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import {
  FaHome, FaDollarSign, FaCalendarAlt, FaEdit, FaTrash,
  FaPhone, FaEnvelope, FaShieldAlt, FaPlus, FaTimes,
  FaArrowUp, FaArrowDown, FaCheckCircle, FaUsers,
  FaHammer, FaTools, FaFilter, FaCity, FaExclamationTriangle, FaBolt,
  FaBuilding, FaUser, FaStickyNote, FaTag, FaCopy, FaInfoCircle,
  FaChevronUp, FaChevronDown, FaReceipt
} from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './PropertyDetails.css';
import './Activity.css';
import LoadingScreen from './LoadingScreen';
import { showToast } from '../components/toast';

const BASE_URL = "https://api.rside.app";

const toInputDate = (d) => {
  if (!d) return '';
  const s = String(d).trim().split('T')[0].split(' ')[0];
  const dp = s.split('-');
  if (dp.length === 3 && dp[0].length <= 2 && dp[2].length === 4)
    return `${dp[2]}-${dp[0].padStart(2,'0')}-${dp[1].padStart(2,'0')}`;
  const sp = s.split('/');
  if (sp.length === 3 && sp[2].length === 4)
    return `${sp[2]}-${sp[0].padStart(2,'0')}-${sp[1].padStart(2,'0')}`;
  return s;
};

function PropertyDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const doorFilter = searchParams.get('door');

  const [initials, setInitials]   = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [pmFullName, setPmFullName] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const askConfirm = (opts, onConfirm) => setConfirmDialog({ ...opts, onConfirm });
  const [property, setProperty]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  // Tabs
  const [activeTab, setActiveTab] = useState('overview');

  // Edit
  const [isEditMode, setIsEditMode]         = useState(false);
  const [editingSection, setEditingSection] = useState(null); // 'property'|'dates'|'financial'|'rent'
  const [editedProperty, setEditedProperty] = useState(null);

  // Work Orders
  const [workOrders, setWorkOrders]         = useState([]);
  const [workOrdersLoading, setWOLoading]   = useState(false);
  const [additionalFees, setAdditionalFees] = useState([]);
  const [showAddWO, setShowAddWO]           = useState(false);
  const [woForm, setWoForm]                 = useState({ title: '', priority: 'Medium', dueDate: '', description: '' });
  const [woSubmitting, setWoSubmitting]     = useState(false);
  const [selectedWO, setSelectedWO]         = useState(null);
  const [woDrawerTimeline, setWoDrawerTimeline] = useState([]);
  const [woDrawerLoading, setWoDrawerLoading]   = useState(false);

  // Other Occupants
  const [showOthers, setShowOthers]   = useState(false);
  const [otherOccs, setOtherOccs]     = useState([]);
  const [showAddOcc, setShowAddOcc]   = useState(false);
  const [addOccForm, setAddOccForm]   = useState({ fullName: '', phone: '' });
  const [occSubmitting, setOccSub]    = useState(false);

  // Reference code copy feedback
  const [copiedCode, setCopiedCode] = useState(false);

  // Activity filter
  const [actFilterType, setActFilterType] = useState('all');   // 'all'|'income'|'expense'|'forgiveness'
  const [actFilterPeriod, setActFilterPeriod] = useState('all'); // 'all'|'month'|'last_month'|'year'
  const [showActFilter, setShowActFilter] = useState(false);
  const actFilterRef = useRef(null);

  // Inline add activity modal
  const [addActType, setAddActType] = useState(null); // null | 'income' | 'expense' | 'fee'
  const [showAddActMenu, setShowAddActMenu] = useState(false);
  const [addExpForm, setAddExpForm] = useState({ title: '', amount: '', date: new Date().toISOString().split('T')[0], category: '', utilityType: '', notes: '', doorId: '' });
  const [addIncForm, setAddIncForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], method: 'Cash', category: 'Rent', month: '', notes: '', forgivenessMonth: '', partial: false, tenantId: null, doorId: '' });
  const [editingExpId, setEditingExpId] = useState(null);
  const [editingIncId, setEditingIncId] = useState(null);
  const [addActSaving, setAddActSaving] = useState(false);
  const [forgivenessResolveError, setForgivenessResolveError] = useState(false);
  const [expectedRent, setExpectedRent] = useState(null);
  // Push fee
  const [availableFees, setAvailableFees] = useState([]);
  const [feeFormLoading, setFeeFormLoading] = useState(false);
  const [feeForm, setFeeForm] = useState({ feeId: '', selectedFee: null, notes: '', doorId: '', tenantId: null });

  // Utilities
  const [utilities, setUtilities] = useState([]);
  const [utilitiesLoading, setUtilitiesLoading] = useState(false);
  const [utilExpanded, setUtilExpanded] = useState(false);
  const [showUtilModal, setShowUtilModal] = useState(false);
  const [editingUtilId, setEditingUtilId] = useState(null);
  const [utilForm, setUtilForm] = useState({ Utility_Type: '', Provider: '', Account_Number: '', Due_Day: '' });
  const [utilSaving, setUtilSaving] = useState(false);

  // PM ID (needed for AddStoredDocs)
  const [pmId, setPmId] = useState(null);

  // Stored Documents
  const [storedDocs, setStoredDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsExpanded, setDocsExpanded] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [docForm, setDocForm] = useState({ document_name: '', share_with_tenant: false, file: null });
  const [docSaving, setDocSaving] = useState(false);

  // Past Tenants
  const [pastTenants, setPastTenants] = useState([]);
  const [pastTenantsLoading, setPastTenantsLoading] = useState(false);
  const [pastTenantsExpanded, setPastTenantsExpanded] = useState(false);
  const [showPastTenantModal, setShowPastTenantModal] = useState(false);
  const [editingPastTenantId, setEditingPastTenantId] = useState(null);
  const [pastTenantForm, setPastTenantForm] = useState({ Full_Name: '', Phone_Number: '', Email_Address: '' });
  const [pastTenantSaving, setPastTenantSaving] = useState(false);

  // Door Assets
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsExpanded, setAssetsExpanded] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [assetForm, setAssetForm] = useState({ Asset: '', Manufacturer: '', Model_Number: '', Install_Date: '', Warranty_Expiration_Date: '' });
  const [assetSaving, setAssetSaving] = useState(false);

  // Activity menu
  const [showActMenu, setShowActMenu] = useState(false);
  const actMenuRef = useRef(null);
  const pendingSaveRef = useRef(null); // { activityType, saveTime } — boosts newly added item to top

  // Selected activity for popup
  const [selectedAct, setSelectedAct] = useState(null);

  // Past due rent for this property
  const [pastDueAmount, setPastDueAmount] = useState(null); // null=loading, number=loaded

  // ── Data fetch ──────────────────────────────────────────────────
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      try {
        setUserEmail(user.email);
        const pmRes = await fetch(`${BASE_URL}/PropertyManagerName?email=${user.email}`);
        const pmData = await pmRes.json();
        const fullName = pmData.Full_Name || '';
        setPmFullName(fullName);
        const parts = fullName.split(' ');
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));

        const propRes = await fetch(`${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(fullName)}&Email=${encodeURIComponent(user.email)}`);
        const propData = await propRes.json();
        const propertyData = (propData.Properties || []).find(p => p.Property_ID === parseInt(id));
        if (!propertyData) { navigate('/properties'); return; }

        const isMultiFamily = parseInt(propertyData.Num_Of_Units) > 1;

        let allDoors = [], propertyDetails = null;
        try {
          const detailsRes = await fetch(`${BASE_URL}/GetPropertyDetails?property_name=${encodeURIComponent(propertyData.Property_Name)}&email=${encodeURIComponent(user.email)}`);
          if (detailsRes.ok) { const d = await detailsRes.json(); allDoors = d.results || []; propertyDetails = allDoors[0] || null; }
        } catch {}

        const doorsToProcess = doorFilter ? allDoors.filter(d => String(d.door_id) === String(doorFilter)) : allDoors;

        const unitsWithTenants = [];
        for (const door of doorsToProcess) {
          let tenantInfo = null;
          if (door.door_id) {
            try {
              const tr = await fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${door.door_id}`);
              if (tr.ok) { const td = await tr.json(); tenantInfo = td.tenants?.[0] || td.tenant || (Array.isArray(td) ? td[0] : null); }
            } catch {}
          }
          const hasLease = tenantInfo?.tenant_id || tenantInfo?.Full_Name;
          const doorIndexInAll = allDoors.findIndex(d => String(d.door_id) === String(door.door_id));
          unitsWithTenants.push({
            doorId: door.door_id, unitNumber: door.Unit_Number || '',
            doorIndexInAll: doorIndexInAll >= 0 ? doorIndexInAll : 0,
            streetAddress: door.Street_Address || '', city: door.City || '',
            state: door.State || '', zipcode: door.Zipcode || '',
            status: door.Stat || (hasLease ? 'Occupied' : 'Vacant'),
            tenantId: tenantInfo?.tenant_id || null, leaseId: tenantInfo?.lease_id || null,
            tenantName: tenantInfo?.Full_Name || '', phoneNumber: tenantInfo?.Phone_Number || '',
            deposit: tenantInfo?.Deposit || '', rent: tenantInfo?.Rent || '',
            leaseStartDate: toInputDate(tenantInfo?.Start_Date),
            leaseEndDate: toInputDate(tenantInfo?.End_Date),
            rentDueDate: tenantInfo?.Rent_Due || '',
            leaseType: tenantInfo?.MonthToMonth ? 'Month-to-Month' : 'Fixed',
            referenceCode: door.referenceCode || '',
          });
        }

        const firstUnit = unitsWithTenants[0] || {};
        let overallStatus = propertyData.Status || 'Vacant';
        if (isMultiFamily && unitsWithTenants.length > 0) {
          const occ = unitsWithTenants.filter(u => u.status === 'Occupied').length;
          overallStatus = occ === unitsWithTenants.length ? 'Occupied' : occ === 0 ? 'Vacant' : 'Partial';
        }

        let financialInfo = null;
        try {
          const fr = await fetch(`${BASE_URL}/PropertyFinancials?property_id=${id}`);
          if (fr.ok) { const fd = await fr.json(); financialInfo = fd.financials?.[0] || null; }
        } catch {}

        const fullProperty = {
          propertyId: propertyData.Property_ID, doorId: firstUnit.doorId || null,
          tenantId: firstUnit.tenantId || null, leaseId: firstUnit.leaseId || null,
          financialId: financialInfo?.id ?? null,
          id: propertyData.Property_ID, name: propertyData.Property_Name,
          propertyType: isMultiFamily ? 'Multi Family Home' : 'Single Family Home',
          numberOfUnits: propertyData.Num_Of_Units, status: overallStatus,
          address: propertyDetails ? `${propertyDetails.Street_Address}, ${propertyDetails.City}, ${propertyDetails.State} ${propertyDetails.Zipcode}` : '',
          streetAddress: propertyDetails?.Street_Address || '', city: propertyDetails?.City || '',
          state: propertyDetails?.State || '', zipcode: propertyDetails?.Zipcode || '',
          unitNumber: propertyDetails?.Unit_Number || '',
          tenantName: firstUnit.tenantName || '', phoneNumber: firstUnit.phoneNumber || '',
          deposit: firstUnit.deposit || '', rent: firstUnit.rent || '',
          leaseStartDate: firstUnit.leaseStartDate || '', leaseEndDate: firstUnit.leaseEndDate || '',
          rentDueDate: firstUnit.rentDueDate || '', leaseType: firstUnit.leaseType || 'Fixed',
          purchasePrice: financialInfo?.Purchase_Price || '', totalCost: financialInfo?.Total_Cost || '',
          renovCost: financialInfo?.Renov_Cost || '', mortgagePayment: financialInfo?.Mortgage_Payment || '',
          propertyTaxes: financialInfo?.Tax || '', propertyInsurance: financialInfo?.Insurance || financialInfo?.Insurance_Annual || financialInfo?.insurance_annual || '',
          insuranceStartDate: toInputDate(financialInfo?.Insurance_Start),
          insuranceEndDate: toInputDate(financialInfo?.Insurance_End),
          hoa: financialInfo?.HOA || '', downPayment: financialInfo?.Down_Payment || '',
          units: unitsWithTenants,
        };

        setProperty(fullProperty);
        setEditedProperty(fullProperty);
        setLoading(false);
        await fetchPropertyActivities(unitsWithTenants, propertyData.Property_Name, user.email);
        await fetchWorkOrders(user.email, unitsWithTenants, propertyData.Property_Name);
        await fetchAdditionalFees(firstUnit.doorId || null);
        // Fetch pm_id for stored docs uploads
        try {
          const acctRes = await fetch(`${BASE_URL}/MyAccount?Email=${encodeURIComponent(user.email)}`);
          if (acctRes.ok) { const acct = await acctRes.json(); setPmId(acct.id || acct.PM_ID || acct.pm_id || null); }
        } catch {}

        await fetchUtilities(firstUnit.doorId || null);
        await fetchAssets(firstUnit.doorId || null);
        await fetchStoredDocs(firstUnit.doorId || null);
        await fetchPastTenants(firstUnit.doorId || null);

        // Fetch past due — when on a specific unit page only fetch that door,
        // on the property-level page sum all doors.
        try {
          const doorIdsToCheck = doorFilter
            ? [doorFilter]
            : allDoors.map(d => d.door_id).filter(Boolean);
          const results = await Promise.allSettled(
            doorIdsToCheck.map(dId =>
              fetch(`${BASE_URL}/GetPastDuePaymentsByDoor?door_id=${dId}`)
                .then(r => r.ok ? r.json() : null)
            )
          );
          const total = results.reduce((sum, r) => {
            if (r.status !== 'fulfilled' || !r.value) return sum;
            return sum + Number(r.value?.summary?.past_due || 0);
          }, 0);
          setPastDueAmount(total);
        } catch { setPastDueAmount(0); }
      } catch (err) { console.error(err); setLoading(false); }
    });
    return unsub;
  }, [navigate, id]);

  // Close filter dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (actFilterRef.current && !actFilterRef.current.contains(e.target)) setShowActFilter(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Clear edit IDs when the add/edit modal closes
  useEffect(() => {
    if (!addActType) { setEditingExpId(null); setEditingIncId(null); }
  }, [addActType]);

  // Auto-fill rent amount when category = Rent
  useEffect(() => {
    if (addActType !== 'income' || addIncForm.category !== 'Rent' || !property) { setExpectedRent(null); return; }
    const doorId = addIncForm.doorId || unit?.doorId;
    const u = doorId ? (property.units || []).find(u => String(u.doorId) === String(doorId)) || unit : unit;
    const rent = u?.rent ? parseFloat(u.rent) : null;
    setExpectedRent(rent);
    if (rent) setAddIncForm(prev => ({ ...prev, amount: String(rent) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addActType, addIncForm.category, addIncForm.doorId]);

  // Fetch tenant ID for rent forgiveness
  useEffect(() => {
    if (addActType !== 'income' || addIncForm.category !== 'Rent Forgiveness' || !property) return;
    const doorId = addIncForm.doorId || unit?.doorId;
    if (!doorId) { setForgivenessResolveError(false); return; }
    setForgivenessResolveError(false);
    let cancelled = false;
    fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${encodeURIComponent(doorId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled) return;
        const t = d?.tenants?.[0] || d?.tenant;
        const tid = t?.id || t?.tenant_id || t?.Tenant_ID || null;
        if (tid) { setAddIncForm(prev => ({ ...prev, tenantId: tid })); setForgivenessResolveError(false); }
        else setForgivenessResolveError(true);
      })
      .catch(() => { if (!cancelled) setForgivenessResolveError(true); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addActType, addIncForm.category, addIncForm.doorId]);

  // Load available fees for push fee modal
  useEffect(() => {
    if (addActType !== 'fee' || !property) { setAvailableFees([]); return; }
    const doorId = feeForm.doorId || unit?.doorId;
    if (!doorId) { setAvailableFees([]); return; }
    setFeeFormLoading(true);
    Promise.all([
      fetch(`${BASE_URL}/GetFeesByDoor?door_id=${doorId}`),
      fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${doorId}`),
    ]).then(async ([feesRes, tenantRes]) => {
      const fees = feesRes.ok ? ((await feesRes.json()).fees || []) : [];
      let tenantId = null;
      if (tenantRes.ok) {
        const td = await tenantRes.json();
        const tenants = td.tenants || td.Tenants || [];
        tenantId = tenants[0]?.tenant_id || tenants[0]?.id || null;
      }
      setAvailableFees(fees);
      setFeeForm(prev => ({ ...prev, tenantId }));
    }).catch(() => {}).finally(() => setFeeFormLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addActType, feeForm.doorId]);

  const getFilteredActivities = () => {
    let filtered = [...activities];
    if (actFilterType === 'income')      filtered = filtered.filter(a => a.amount > 0 && a.activityType !== 'rent_forgiveness');
    if (actFilterType === 'expense')     filtered = filtered.filter(a => a.amount < 0);
    if (actFilterType === 'forgiveness') filtered = filtered.filter(a => a.activityType === 'rent_forgiveness');
    if (actFilterPeriod !== 'all') {
      const now = new Date();
      filtered = filtered.filter(a => {
        const d = new Date(a.rawDate || a.date || a.monthOf || '');
        if (isNaN(d)) return true;
        if (actFilterPeriod === 'month')      return d >= new Date(now.getFullYear(), now.getMonth(), 1);
        if (actFilterPeriod === 'last_month') return d >= new Date(now.getFullYear(), now.getMonth() - 1, 1) && d < new Date(now.getFullYear(), now.getMonth(), 1);
        if (actFilterPeriod === 'year')       return d >= new Date(now.getFullYear(), 0, 1);
        return true;
      });
    }
    if (doorFilter && (property.units || []).length > 1) {
      filtered = filtered.filter(a => !a.doorId || String(a.doorId) === String(doorFilter));
    }
    return filtered;
  };

  const handlePDSaveExpense = async () => {
    if (!addExpForm.title.trim() || !addExpForm.amount || !addExpForm.date || !addExpForm.category) {
      showToast('Please fill in all required fields.', 'warning'); return;
    }
    if ((addExpForm.category === 'Utility' || addExpForm.category === 'Operation Expense') && !addExpForm.utilityType) {
      showToast('Please select a type.', 'warning'); return;
    }
    setAddActSaving(true);
    try {
      const resolvedCategory =
        addExpForm.category === 'Operation Expense'
          ? (addExpForm.utilityType === 'General' || !addExpForm.utilityType ? 'Operation Expense' : addExpForm.utilityType)
          : addExpForm.category === 'Utility'
          ? (addExpForm.utilityType === 'General' || !addExpForm.utilityType ? 'Utility' : addExpForm.utilityType)
          : addExpForm.category;
      const params = new URLSearchParams({
        title: addExpForm.title.trim(),
        notes: addExpForm.notes || '',
        amount: parseFloat(addExpForm.amount),
        paydate: addExpForm.date + 'T' + new Date().toTimeString().slice(0, 8),
        category: resolvedCategory,
        property_id: parseInt(property.id, 10),
        door_id: parseInt(addExpForm.doorId || unit.doorId, 10),
        source: 'App',
      });
      if (editingExpId) params.append('expense_id', editingExpId);
      const expUrl = editingExpId ? `${BASE_URL}/EditExpense?${params}` : `${BASE_URL}/CreateExpense?${params}`;
      const res = await fetch(expUrl, { method: editingExpId ? 'PUT' : 'POST', headers: { accept: 'application/json' } });
      if (res.ok) {
        localStorage.setItem('rside_pd_activity_boost', JSON.stringify({ activityType: 'expense', setAt: Date.now() }));
        setAddActType(null);
        setAddExpForm({ title: '', amount: '', date: new Date().toISOString().split('T')[0], category: '', utilityType: '', notes: '', doorId: '' });
        await fetchPropertyActivities(property.units || [unit]);
      } else { showToast(editingExpId ? 'Failed to update expense.' : 'Failed to add expense.', 'error'); }
    } catch { showToast('Error saving expense.', 'error'); }
    setAddActSaving(false);
  };

  const resetIncForm = () => setAddIncForm({ amount: '', date: new Date().toISOString().split('T')[0], method: 'Cash', category: 'Rent', month: '', notes: '', forgivenessMonth: '', partial: false, tenantId: null, doorId: '' });

  const handlePDEditActivity = (act) => {
    setSelectedAct(null);
    if (act.activityType === 'expense') {
      const _opSubs = ['HOA', 'Tax', 'Insurance', 'Mortgage'];
      const _utilSubs = ['Water', 'Gas', 'Electricity', 'Trash', 'Sewer'];
      const _rawCat = act.type || '';
      const _expCat = _opSubs.includes(_rawCat) ? 'Operation Expense'
        : _utilSubs.includes(_rawCat) ? 'Utility'
        : _rawCat;
      const _expUtil = (_opSubs.includes(_rawCat) || _utilSubs.includes(_rawCat)) ? _rawCat : '';
      setEditingExpId(act.expenseId || String(act.id || '').replace('expense-', ''));
      setAddExpForm({
        title: act.vendor || '',
        amount: String(Math.abs(act.amount)),
        date: act.rawDate ? act.rawDate.substring(0, 10) : new Date().toISOString().split('T')[0],
        category: _expCat,
        utilityType: _expUtil,
        notes: act.notes || '',
        doorId: act.doorId ? String(act.doorId) : '',
      });
      setAddActType('expense');
    } else if (act.activityType === 'rent') {
      setEditingIncId(String(act.rentId || String(act.id || '').replace('rent-', '')));
      setAddIncForm({
        amount: String(Math.abs(act.amount)),
        date: act.rawDate ? act.rawDate.substring(0, 10) : new Date().toISOString().split('T')[0],
        method: act.method || '',
        category: act.category || 'Rent',
        month: act.monthOf ? act.monthOf.split(' ')[0] : '',
        notes: act.notes || '',
        forgivenessMonth: '',
        partial: !!(act.isPartial),
        tenantId: null,
        doorId: act.doorId ? String(act.doorId) : '',
      });
      setAddActType('income');
    } else if (act.activityType === 'rent_forgiveness') {
      const fgMonth = act.rawMonthOf ? act.rawMonthOf.substring(0, 7) : (act.rawDate ? act.rawDate.substring(0, 7) : '');
      setEditingIncId(String(act.forgiveness_id || String(act.id || '').replace('rent-', '')));
      setAddIncForm({
        amount: String(Math.abs(act.amount)),
        date: act.rawDate ? act.rawDate.substring(0, 10) : new Date().toISOString().split('T')[0],
        method: '',
        category: 'Rent Forgiveness',
        month: '',
        notes: act.notes || '',
        forgivenessMonth: fgMonth,
        partial: false,
        tenantId: null,
        doorId: act.doorId ? String(act.doorId) : '',
      });
      setAddActType('income');
    }
  };

  const handlePDSaveIncome = async () => {
    const doorId = addIncForm.doorId || unit?.doorId;
    // ── Rent Forgiveness ──
    if (addIncForm.category === 'Rent Forgiveness') {
      if (!addIncForm.amount || !addIncForm.forgivenessMonth) { showToast('Please fill in amount and month.', 'warning'); return; }
      if (!addIncForm.tenantId) { showToast('Could not resolve tenant. Please try again.', 'error'); return; }
      setAddActSaving(true);
      try {
        const _now = new Date();
        const _pad = n => String(n).padStart(2, '0');
        const _paydate = `${_now.getFullYear()}-${_pad(_now.getMonth()+1)}-${_pad(_now.getDate())}T${_pad(_now.getHours())}:${_pad(_now.getMinutes())}:${_pad(_now.getSeconds())}`;
        let fgRes;
        if (editingIncId) {
          const editParams = new URLSearchParams({
            rent_forgiveness_id: editingIncId,
            amount: parseFloat(addIncForm.amount),
            month_of: addIncForm.forgivenessMonth + '-01',
            tenant_id: addIncForm.tenantId,
          });
          if (addIncForm.notes) editParams.append('reason', addIncForm.notes);
          fgRes = await fetch(`${BASE_URL}/EditRentForgiveness?${editParams}`, { method: 'PUT', headers: { accept: 'application/json' } });
        } else {
          const params = new URLSearchParams({
            door_id: parseInt(doorId, 10),
            tenant_id: addIncForm.tenantId,
            amount: parseFloat(addIncForm.amount),
            month_of: addIncForm.forgivenessMonth + '-01',
            paydate: _paydate,
          });
          if (addIncForm.notes) params.append('reason', addIncForm.notes);
          fgRes = await fetch(`${BASE_URL}/AddRentForgiveness?${params}`, { method: 'POST', headers: { accept: 'application/json' } });
        }
        const res = fgRes;
        if (res.ok) { setAddActType(null); resetIncForm(); await fetchPropertyActivities(property.units || [unit]); }
        else { const err = await res.text().catch(() => ''); showToast(`Failed to save rent forgiveness (${res.status}): ${err}`, 'error'); }
      } catch { showToast('Error saving rent forgiveness.', 'error'); }
      setAddActSaving(false);
      return;
    }
    // ── Regular income ──
    if (!addIncForm.amount || !addIncForm.date || !addIncForm.method) {
      showToast('Please fill in all required fields.', 'warning'); return;
    }
    if (addIncForm.category === 'Rent' && !addIncForm.month) {
      showToast('Please select a Month Of.', 'warning'); return;
    }
    setAddActSaving(true);
    try {
      const params = new URLSearchParams({
        door_id: parseInt(doorId, 10),
        amount: parseFloat(addIncForm.amount),
        paydate: addIncForm.date + 'T' + new Date().toTimeString().slice(0, 8),
        method: addIncForm.method,
        Source: 'App',
        Category: addIncForm.category,
        partial_payment: (expectedRent !== null && parseFloat(addIncForm.amount) < expectedRent) ? 1 : 0,
        fees_added: addIncForm.category === 'Fee' ? parseFloat(addIncForm.amount) : 0,
      });
      if (addIncForm.notes) params.append('notes', addIncForm.notes);
      if (editingIncId) params.append('rent_id', editingIncId);
      const incUrl = editingIncId ? `${BASE_URL}/EditIncome?${params}` : `${BASE_URL}/AddRent?${params}`;
      const res = await fetch(incUrl, { method: editingIncId ? 'PUT' : 'POST', headers: { accept: 'application/json' } });
      if (res.ok) {
        localStorage.setItem('rside_pd_activity_boost', JSON.stringify({ activityType: 'rent', setAt: Date.now() }));
        setAddActType(null); resetIncForm();
        await fetchPropertyActivities(property.units || [unit]);
      } else { showToast(editingIncId ? 'Failed to update income.' : 'Failed to add income.', 'error'); }
    } catch { showToast('Error saving income.', 'error'); }
    setAddActSaving(false);
  };

  const handlePDSaveFee = async () => {
    const doorId = feeForm.doorId || unit?.doorId;
    if (!feeForm.feeId) { showToast('Please select a fee.', 'warning'); return; }
    if (!doorId) { showToast('Could not resolve unit.', 'warning'); return; }
    if (!feeForm.tenantId) { showToast('Could not resolve tenant. Please try again.', 'error'); return; }
    setAddActSaving(true);
    try {
      const _now = new Date();
      const _pad = n => String(n).padStart(2, '0');
      const _paydate = `${_now.getFullYear()}-${_pad(_now.getMonth()+1)}-${_pad(_now.getDate())}T${_pad(_now.getHours())}:${_pad(_now.getMinutes())}:${_pad(_now.getSeconds())}`;
      const params = new URLSearchParams({ fee_id: feeForm.feeId, door_id: doorId, tenant_id: feeForm.tenantId, paydate: _paydate });
      if (feeForm.notes) params.append('notes', feeForm.notes);
      const res = await fetch(`${BASE_URL}/AddTenantFee?${params}`, { method: 'POST', headers: { accept: 'application/json' } });
      if (res.ok) { setAddActType(null); setFeeForm({ feeId: '', selectedFee: null, notes: '', doorId: '', tenantId: null }); await fetchPropertyActivities(property.units || [unit]); }
      else { showToast('Failed to push fee.', 'error'); }
    } catch { showToast('Error pushing fee.', 'error'); }
    setAddActSaving(false);
  };

  const fetchPropertyActivities = async (units, propNameOverride, emailOverride) => {
    setActivitiesLoading(true);

    const MONTH_MAP = {January:1,February:2,March:3,April:4,May:5,June:6,July:7,August:8,September:9,October:10,November:11,December:12};
    const parseMonthOf = (s) => {
      if (!s) return '';
      if (/^\d{4}-\d{2}/.test(s)) return s;
      const [name, yr] = s.split(' ');
      const mo = MONTH_MAP[name];
      return (mo && yr) ? `${yr}-${String(mo).padStart(2,'0')}-01` : '';
    };
    const fmtDate = (rawDate) => rawDate
      ? new Date(/^\d{4}-\d{2}-\d{2}/.test(rawDate) ? rawDate.substring(0,10) + 'T00:00:00' : rawDate).toLocaleDateString()
      : '';

    try {
      const propName = propNameOverride || property?.name || '';

      // ── Door-level view: use the 3 parallel door-specific endpoints ──
      if (doorFilter) {
        const [expRes, rentRes] = await Promise.allSettled([
          fetch(`${BASE_URL}/ExpensesByDoor?door_id=${doorFilter}`),
          fetch(`${BASE_URL}/RentByDoor?door_id=${doorFilter}`),
        ]);

        const all = [];
        const doorIdStr = String(doorFilter);

        // 1 — Expenses
        if (expRes.status === 'fulfilled' && expRes.value.ok) {
          const expData = await expRes.value.json();
          const expenses = Array.isArray(expData) ? expData
            : Array.isArray(expData.expenses) ? expData.expenses
            : Array.isArray(expData.Expenses) ? expData.Expenses : [];
          expenses.forEach((e, idx) => {
            const rawDate = e.paydateString || e.paydate || e.PayDate || e.Paydate || '';
            const tsStr = e.timestampString || rawDate;
            all.push({
              id: `expense-${e.expense_id || e.id || idx}`,
              expenseId: e.expense_id || e.id || null,
              sortId: parseInt(e.expense_id || e.id || 0, 10),
              amount: -Math.abs(Number(e.Amount || e.amount || 0)),
              type: e.Category || e.category || 'Expense',
              propertyName: propName,
              vendor: e.title || e.Title || '',
              rawDate: tsStr, date: fmtDate(rawDate),
              notes: e.Notes || e.notes || '',
              unitNumber: e.Unit_Number || '',
              doorId: doorIdStr,
              activityType: 'expense',
              category: e.Category || e.category || 'Expense',
            });
          });
        }

        // 2 — Rent (includes forgiveness items)
        if (rentRes.status === 'fulfilled' && rentRes.value.ok) {
          let rents = await rentRes.value.json();
          rents = Array.isArray(rents.items) ? rents.items
            : Array.isArray(rents) ? rents
            : Array.isArray(rents.rent_payments) ? rents.rent_payments
            : Array.isArray(rents.RentPayments) ? rents.RentPayments : [];
          while (Array.isArray(rents) && Array.isArray(rents[0])) rents = rents.flat();
          rents.forEach((r, idx) => {
            const rawDate = r.paydateString || r.Paydate || r.paydate || r.PayDate || '';
            const tsStr = r.timestampString || rawDate;
            const monthOfDisplay = r.MonthOf || '';
            if (r.item_type === 'rent_forgiveness' || r.rent_forgiveness_id) {
              all.push({
                id: `rent-${r.rent_forgiveness_id || r.id || idx}`,
                rentId: r.rent_forgiveness_id || null,
                sortId: parseInt(r.rent_forgiveness_id || r.id || 0, 10),
                amount: Math.abs(Number(r.Amount || r.amount || 0)),
                type: 'Rent Forgiveness',
                propertyName: propName,
                vendor: r.Full_Name || 'Tenant',
                rawDate: tsStr, date: fmtDate(rawDate),
                notes: r.Notes || r.Reason || '',
                monthOf: monthOfDisplay,
                rawMonthOf: parseMonthOf(monthOfDisplay),
                activityType: 'rent_forgiveness',
                isPartial: false,
                category: 'Rent Forgiveness',
                forgiveness_id: r.rent_forgiveness_id || r.id,
                unitNumber: r.Unit_Number || '',
                doorId: doorIdStr,
              });
            } else {
              all.push({
                id: `rent-${r.rent_id || r.id || idx}`,
                rentId: r.rent_id || r.id || null,
                sortId: parseInt(r.rent_id || r.id || 0, 10),
                amount: Math.abs(Number(r.Amount || r.amount || r.rent || r.Rent || 0)),
                type: (r.Category || r.category) === 'Other' ? 'Income' : 'Rent',
                propertyName: propName,
                vendor: r.Full_Name || r.Method || 'Tenant',
                method: r.Method || r.method || '',
                rawDate: tsStr, date: fmtDate(rawDate),
                notes: r.Notes || r.notes || '',
                monthOf: monthOfDisplay,
                rawMonthOf: parseMonthOf(monthOfDisplay),
                activityType: 'rent',
                isPartial: !!(r.Partial_payment),
                category: r.Category || r.category || 'Rent',
                forgiveness_id: null,
                unitNumber: r.Unit_Number || '',
                doorId: doorIdStr,
              });
            }
          });
        }


        all.sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));
        pendingSaveRef.current = null;
        setActivities(all);
        setActivitiesLoading(false);
        return;
      }

      // ── Property-level view: use ActivityFeed ──
      const email = emailOverride || userEmail;
      if (!email || !propName) return;
      const _now = new Date();
      const res = await fetch(`${BASE_URL}/ActivityFeed?pm_email=${encodeURIComponent(email)}&end_year=${_now.getFullYear()}&end_month=12`);
      if (!res.ok) throw new Error(`ActivityFeed ${res.status}`);
      const data = await res.json();

      const all = (data.items || [])
        .filter(item => item.Property_Name === propName)
        .map((item, idx) => {
          const rawDate = item.paydateString || '';
          const tsStr = item.timestampString || rawDate;
          const date = fmtDate(rawDate);
          const itemDoorId = String(item.door_id || item.Door_id || item.Door_ID || '');
          if (item.item_type === 'expense') {
            return {
              id: `expense-${item.expense_id || item.id || idx}`,
              expenseId: item.expense_id || item.id || null,
              sortId: parseInt(item.expense_id || item.id || 0, 10),
              amount: -Math.abs(Number(item.Amount || 0)),
              type: item.Category || 'Expense',
              propertyName: item.Property_Name || '',
              vendor: item.title || '',
              rawDate: tsStr, date,
              notes: item.Notes || '',
              unitNumber: item.Unit_Number || '',
              doorId: itemDoorId,
              activityType: 'expense',
              category: item.Category || 'Expense',
            };
          }
          if (item.item_type === 'rent_forgiveness') {
            const monthOfDisplay = item.MonthOf || '';
            return {
              id: `rent-${item.rent_forgiveness_id || item.id || idx}`,
              rentId: item.rent_forgiveness_id || item.id || null,
              sortId: parseInt(item.rent_forgiveness_id || item.id || 0, 10),
              amount: Math.abs(Number(item.Amount || 0)),
              type: 'Rent Forgiveness',
              propertyName: item.Property_Name || '',
              vendor: item.Full_Name || 'Tenant',
              rawDate: tsStr, date,
              notes: item.Notes || item.Reason || '',
              monthOf: monthOfDisplay,
              rawMonthOf: parseMonthOf(monthOfDisplay),
              activityType: 'rent_forgiveness',
              isPartial: false,
              category: 'Rent Forgiveness',
              forgiveness_id: item.rent_forgiveness_id || item.id,
              unitNumber: item.Unit_Number || '',
              doorId: itemDoorId,
            };
          }
          if (item.item_type === 'rent') {
            const monthOfDisplay = item.MonthOf || '';
            return {
              id: `rent-${item.rent_id || item.id || idx}`,
              rentId: item.rent_id || item.id || null,
              sortId: parseInt(item.rent_id || item.id || 0, 10),
              amount: Math.abs(Number(item.Amount || 0)),
              type: item.Category === 'Other' ? 'Income' : 'Rent',
              propertyName: item.Property_Name || '',
              vendor: item.Full_Name || item.Method || 'Tenant',
              method: item.Method || '',
              rawDate: tsStr, date,
              notes: item.Notes || '',
              monthOf: monthOfDisplay,
              rawMonthOf: parseMonthOf(monthOfDisplay),
              activityType: 'rent',
              isPartial: !!(item.Partial_payment),
              category: item.Category || 'Rent',
              forgiveness_id: null,
              unitNumber: item.Unit_Number || '',
              doorId: itemDoorId,
            };
          }
          if (item.item_type === 'pushed_fee') {
            return {
              id: `fee-${item.pushed_fee_id || item.id || idx}`,
              sortId: parseInt(item.pushed_fee_id || item.id || 0, 10),
              amount: Math.abs(Number(item.Amount || 0)),
              type: item.Fee_Type || 'Pushed Fee',
              propertyName: item.Property_Name || '',
              vendor: item.Full_Name || 'Tenant',
              rawDate: tsStr, date,
              notes: item.Notes || '',
              activityType: 'pushed_fee',
              category: 'Pushed Fee',
              status: (item.Status || 'pending').toLowerCase(),
              pushedFeeId: item.pushed_fee_id || null,
              unitNumber: item.Unit_Number || '',
              tenantId: item.Tenant_id,
              doorId: itemDoorId,
            };
          }
          return null;
        }).filter(Boolean);


      all.sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));
      pendingSaveRef.current = null;
      setActivities(all);
    } catch (err) {
      console.error('fetchPropertyActivities error:', err);
    }
    setActivitiesLoading(false);
  };

  const openWODrawer = async (wo) => {
    setSelectedWO(wo);
    setWoDrawerTimeline([]);
    setWoDrawerLoading(true);
    try {
      const emailToName = pmFullName ? { [userEmail]: pmFullName } : {};
      const res = await fetch(`${BASE_URL}/GetWorkOrderTimeline?work_order_id=${encodeURIComponent(wo.ID)}`);
      if (res.ok) {
        const d = await res.json();
        const raw = d.timeline || d.Timeline || (Array.isArray(d) ? d : []);
        setWoDrawerTimeline(raw.map(t => ({
          ...t,
          _author: emailToName[t.Updated_By] || emailToName[t.updated_by] || t.Updated_By_Name || t.Full_Name || t.Updated_By || t.updated_by || '',
        })));
      }
    } catch {}
    setWoDrawerLoading(false);
  };

  const fetchWorkOrders = async (email, units, propName) => {
    setWOLoading(true);
    try {
      const mapPriority = (p) => p === 3 || p === '3' ? 'Urgent' : p === 1 || p === '1' ? 'Low' : 'Medium';

      // ── Door-level view: fetch only this door's work orders ──
      if (doorFilter) {
        const r = await fetch(`${BASE_URL}/TenantApp/GetWorkOrders?door_id=${doorFilter}`);
        if (r.ok) {
          const d = await r.json();
          setWorkOrders((d.work_orders || []).map(wo => ({
            ID: wo.id,
            Title: wo.Title || 'Untitled',
            Status: wo.Stat || 'Open',
            Priority: mapPriority(wo.Priority),
            Description: wo.Details || '',
            Unit_Number: wo.Unit_Number || '',
            Property_Name: wo.Property_Name || property?.name || '',
            Date_Submitted: wo.Create_Date || '',
            Due_Date: wo.Due_Date || '',
            Attached_Expenses: wo.Attached_Expenses || [],
            Door_id: String(doorFilter),
          })));
        }
        setWOLoading(false);
        return;
      }

      // ── Property-level view: fetch all PM work orders and filter by property ──
      const pmEmail = email || userEmail;
      if (!pmEmail) return;
      const r = await fetch(`${BASE_URL}/GetWorkOrdersByPM?pm_email=${encodeURIComponent(pmEmail)}`);
      if (r.ok) {
        const d = await r.json();
        const doorIds = new Set((units || property?.units || []).map(u => String(u.doorId)).filter(Boolean));
        const name = (propName || property?.name || '').toLowerCase().trim();
        const relevant = (d.work_orders || []).filter(wo => {
          const woDoor = String(wo.door_id || wo.Door_id || wo.Door_ID || '');
          if (woDoor && doorIds.has(woDoor)) return true;
          return (wo.Property_Name || '').toLowerCase().trim() === name;
        });
        setWorkOrders(relevant.map(wo => ({
          ID: wo.id,
          Title: wo.Title || 'Untitled',
          Status: wo.Stat || 'Open',
          Priority: mapPriority(wo.Priority),
          Description: wo.Details || '',
          Unit_Number: wo.Unit_Number || '',
          Property_Name: wo.Property_Name || '',
          Date_Submitted: wo.Create_Date || '',
          Due_Date: wo.Due_Date || '',
          Attached_Expenses: wo.Attached_Expenses || [],
          Door_id: wo.door_id || wo.Door_id || wo.Door_ID || '',
        })));
      }
    } catch {}
    setWOLoading(false);
  };

  const fetchStoredDocs = async (doorId) => {
    if (!doorId) return;
    setDocsLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/GetStoredDocsByDoor?door_id=${doorId}`);
      if (r.ok) { const d = await r.json(); setStoredDocs(d.documents || []); }
    } catch {}
    setDocsLoading(false);
  };

  const fetchAssets = async (doorId) => {
    if (!doorId) return;
    setAssetsLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/GetDoorAssets?Door_id=${doorId}`);
      if (r.ok) { const d = await r.json(); setAssets(Array.isArray(d) ? d : []); }
    } catch {}
    setAssetsLoading(false);
  };

  const fetchUtilities = async (doorId) => {
    if (!doorId) return;
    setUtilitiesLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/GetUtilities?Door_id=${doorId}`);
      if (r.ok) { const d = await r.json(); setUtilities(Array.isArray(d) ? d : []); }
    } catch {}
    setUtilitiesLoading(false);
  };

  const fetchAdditionalFees = async (doorId) => {
    if (!doorId) return;
    try {
      const r = await fetch(`${BASE_URL}/GetFeesByDoor?door_id=${doorId}`);
      if (r.ok) {
        const d = await r.json();
        setAdditionalFees(d.fees || (Array.isArray(d) ? d : []));
      }
    } catch {}
  };

  const fetchOtherOccs = async () => {
    if (!property?.doorId) return;
    try {
      const r = await fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${property.doorId}`);
      if (r.ok) {
        const d = await r.json();
        const all = d.tenants || (Array.isArray(d) ? d : []);
        const sorted = [...all].sort((a, b) => (a.tenant_id || 0) - (b.tenant_id || 0));
        setOtherOccs(sorted.slice(1));
      }
    } catch {}
  };

  const handleOthersClick = () => {
    setShowOthers(true);
    fetchOtherOccs();
  };

  const submitAddOcc = async () => {
    if (!addOccForm.fullName.trim()) return;
    setOccSub(true);
    try {
      const params = new URLSearchParams({ full_name: addOccForm.fullName, phone_number: addOccForm.phone || '', door_id: property.doorId });
      const r = await fetch(`${BASE_URL}/AddNonPrimaryTenant?${params}`, { method: 'POST' });
      if (r.ok) { setShowAddOcc(false); setAddOccForm({ fullName: '', phone: '' }); fetchOtherOccs(); }
    } catch {}
    setOccSub(false);
  };

  const submitWorkOrder = async () => {
    if (!woForm.title.trim() || !woForm.description.trim()) return;
    setWoSubmitting(true);
    try {
      let priorityValue = '2';
      if (woForm.priority === 'Urgent') priorityValue = '3';
      if (woForm.priority === 'Low') priorityValue = '1';
      const formData = new FormData();
      formData.append('title', woForm.title.trim());
      formData.append('priority', priorityValue);
      formData.append('details', woForm.description.trim());
      formData.append('door_id', property.doorId);
      if (woForm.dueDate) formData.append('due_date', woForm.dueDate);
      formData.append('CreatedBy', 'Manager');
      const existingIds = new Set(workOrders.map(w => String(w.ID)));
      const r = await fetch(`${BASE_URL}/AddWorkOrder`, { method: 'POST', body: formData });
      if (r.ok) {
        const user = auth.currentUser;
        const freshRes = await fetch(`${BASE_URL}/GetWorkOrdersByPM?pm_email=${encodeURIComponent(userEmail)}`);
        if (freshRes.ok) {
          const freshData = await freshRes.json();
          const newEntry = (freshData.work_orders || []).find(w => !existingIds.has(String(w.id)));
          if (newEntry?.id) {
            await fetch(
              `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent('Work Order Created')}&updated_by=${encodeURIComponent(pmFullName || user?.email || 'Manager')}&work_order_id=${newEntry.id}`,
              { method: 'POST' }
            );
          }
        }
        setShowAddWO(false);
        setWoForm({ title: '', priority: 'Medium', dueDate: '', description: '' });
        fetchWorkOrders();
      }
    } catch {}
    setWoSubmitting(false);
  };

  // Convert YYYY-MM-DD → MM-DD-YYYY for APIs that treat YYYY-MM-DD as UTC midnight
  const toMMDDYYYY = (d) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${m}-${day}-${y}`; };

  const addOneYear = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-').map(Number);
    if (!y || !m || !day) return '';
    return `${y + 1}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedProperty(prev => {
      const u = { [name]: name === 'phoneNumber' ? formatPhone(value) : value };
      if (name === 'leaseStartDate' && value) u.leaseEndDate = addOneYear(value);
      if (name === 'insuranceStartDate' && value) u.insuranceEndDate = addOneYear(value);
      return { ...prev, ...u };
    });
  };

  const handleUnitChange = (index, field, value) => {
    setEditedProperty(prev => {
      const units = [...(prev.units || [])];
      units[index] = { ...units[index], [field]: value };
      if (field === 'leaseStartDate' && value) units[index].leaseEndDate = addOneYear(value);
      return { ...prev, units };
    });
  };

  const handleSave = async () => {
    const errors = [];
    const isMF = editedProperty.propertyType === 'Multi Family Home';

    if (isMF) {
      for (const unit of editedProperty.units || []) {
        if (!unit.doorId) continue;
        const orig = (property.units || []).find(u => u.doorId === unit.doorId);
        if (!orig || unit.status !== orig.status) {
          const r = await fetch(`${BASE_URL}/UpdateDoorStatus?${new URLSearchParams({ door_id: unit.doorId, status: unit.status || 'Vacant' })}`, { method: 'PUT' }).catch(() => null);
          if (r && !r.ok) errors.push(`Unit ${unit.unitNumber}: door status`);
        }
        if (unit.status !== 'Vacant' && unit.tenantId && unit.leaseId) {
          const params = new URLSearchParams({ tenant_id: unit.tenantId, full_name: unit.tenantName || '', phone_number: unit.phoneNumber || '', primary_contact: true, lease_id: unit.leaseId, rent: parseFloat(unit.rent)||0, deposit: parseFloat(unit.deposit)||0, start_date: unit.leaseStartDate||'', end_date: unit.leaseEndDate||'', rent_due: parseInt(unit.rentDueDate)||1, month_to_month: unit.leaseType === 'Month-to-Month' });
          const r = await fetch(`${BASE_URL}/UpdateTenantAndLease?${params}`, { method: 'PUT' }).catch(() => null);
          if (r && !r.ok) errors.push(`Unit ${unit.unitNumber}: tenant/lease`);
        }
      }
    } else {
      if (editedProperty.propertyId && editedProperty.doorId) {
        const pp = new URLSearchParams({ property_id: editedProperty.propertyId, property_name: editedProperty.name||'', door_id: editedProperty.doorId, street_address: editedProperty.streetAddress||'', city: editedProperty.city||'', state: editedProperty.state||'', unit_number: editedProperty.unitNumber||'1', zipcode: editedProperty.zipcode||'' });
        const r = await fetch(`${BASE_URL}/UpdatePropertyAndDoorDetails?${pp}`, { method: 'PUT' }).catch(() => null);
        if (r && !r.ok) errors.push('Property/door details');
        if (editedProperty.status !== property.status) {
          const r2 = await fetch(`${BASE_URL}/UpdateDoorStatus?${new URLSearchParams({ door_id: editedProperty.doorId, status: editedProperty.status||'Vacant' })}`, { method: 'PUT' }).catch(() => null);
          if (r2 && !r2.ok) errors.push('Door status');
        }
        if (editedProperty.status !== 'Vacant' && editedProperty.tenantId && editedProperty.leaseId) {
          const tp = new URLSearchParams({ tenant_id: editedProperty.tenantId, full_name: editedProperty.tenantName||'', phone_number: editedProperty.phoneNumber||'', primary_contact: true, lease_id: editedProperty.leaseId, rent: parseFloat(editedProperty.rent)||0, deposit: parseFloat(editedProperty.deposit)||0, start_date: editedProperty.leaseStartDate||'', end_date: editedProperty.leaseEndDate||'', rent_due: parseInt(editedProperty.rentDueDate)||1, month_to_month: editedProperty.leaseType === 'Month-to-Month' });
          const r3 = await fetch(`${BASE_URL}/UpdateTenantAndLease?${tp}`, { method: 'PUT' }).catch(() => null);
          if (r3 && !r3.ok) errors.push('Tenant/lease');
        }
      }
    }

    if (editedProperty.financialId) {
      const roundDollar = (v) => Math.round(parseFloat(v) || 0);
      const calcTotal = roundDollar(editedProperty.purchasePrice) + roundDollar(editedProperty.renovCost);
      const fp = new URLSearchParams({
        financial_id: editedProperty.financialId,
        purchase_price: roundDollar(editedProperty.purchasePrice),
        down_payment: roundDollar(editedProperty.downPayment),
        total_cost: calcTotal,
        Renov_Cost: roundDollar(editedProperty.renovCost),
        mortgage_payment: roundDollar(editedProperty.mortgagePayment),
        tax: roundDollar(editedProperty.propertyTaxes),
        insurance: roundDollar(editedProperty.propertyInsurance),
        hoa: roundDollar(editedProperty.hoa),
      });
      const r = await fetch(`${BASE_URL}/UpdateFinancial?${fp}`, { method: 'PUT' }).catch(() => null);
      if (r && !r.ok) errors.push('Financials');
    }
    const insuranceChanged =
      editedProperty.insuranceStartDate !== property.insuranceStartDate ||
      editedProperty.insuranceEndDate !== property.insuranceEndDate ||
      String(editedProperty.propertyInsurance) !== String(property.propertyInsurance);
    if (insuranceChanged && (editedProperty.insuranceStartDate || editedProperty.insuranceEndDate || editedProperty.propertyInsurance)) {
      const ip = new URLSearchParams({ property_name: editedProperty.name, email: userEmail });
      if (editedProperty.propertyId) ip.set('property_id', editedProperty.propertyId);
      if (editedProperty.propertyInsurance) ip.set('insurance_annual', Math.round(parseFloat(editedProperty.propertyInsurance) || 0));
      if (editedProperty.insuranceStartDate) ip.set('insurance_start', toMMDDYYYY(editedProperty.insuranceStartDate));
      if (editedProperty.insuranceEndDate) ip.set('insurance_end', toMMDDYYYY(editedProperty.insuranceEndDate));
      const ir = await fetch(`${BASE_URL}/UpdateInsuranceDates?${ip}`, { method: 'PUT' }).catch(e => console.error('Insurance save error:', e));
      if (ir && !ir.ok) { const t = await ir.text(); console.error('Insurance API error:', ir.status, t); errors.push('Insurance dates'); }
    }

    const updated = { ...editedProperty, address: `${editedProperty.streetAddress}, ${editedProperty.city}, ${editedProperty.state} ${editedProperty.zipcode}`, totalCost: (parseFloat(editedProperty.purchasePrice)||0) + (parseFloat(editedProperty.renovCost)||0) };
    if (!isMF && updated.units?.length > 0) {
      updated.units = [{ ...updated.units[0], status: updated.status, tenantName: updated.tenantName, phoneNumber: updated.phoneNumber, deposit: updated.deposit, rent: updated.rent, leaseStartDate: updated.leaseStartDate, leaseEndDate: updated.leaseEndDate, rentDueDate: updated.rentDueDate, leaseType: updated.leaseType }];
    }
    setProperty(updated);
    setEditedProperty(updated);
    setEditingSection(null);
    if (errors.length > 0) showToast(`Saved with issues:\n• ${errors.join('\n• ')}`, 'info');
  };

  const handleDelete = () => {
    askConfirm(
      { title: 'Delete Property', message: 'Delete this property? This will also delete all tenants and leases.', confirmLabel: 'Delete', variant: 'danger' },
      async () => { try {
      for (const unit of property.units || []) {
        if (unit.doorId) {
          await fetch(`${BASE_URL}/DeleteLeaseByDoor?door_id=${unit.doorId}`, { method: 'DELETE' }).catch(() => {});
          await fetch(`${BASE_URL}/DeleteTenantsByDoor?door_id=${unit.doorId}`, { method: 'DELETE' }).catch(() => {});
        }
      }
      const r = await fetch(`${BASE_URL}/DeleteProperty?property_id=${property.propertyId}`, { method: 'DELETE' });
      if (r.ok) { navigate('/properties'); } else { showToast('Failed to delete property.', 'error'); }
    } catch { showToast('Error deleting property.', 'error'); } }
    );
  };

  const getActiveDoorId = () => {
    if (!property) return null;
    if (doorFilter) return (property.units || []).find(u => String(u.doorId) === String(doorFilter))?.doorId || property.doorId;
    return property.units?.[0]?.doorId || property.doorId;
  };

  const handleOpenAddUtil = () => {
    setEditingUtilId(null);
    setUtilForm({ Utility_Type: '', Provider: '', Account_Number: '', Due_Day: '' });
    setShowUtilModal(true);
  };

  const handleOpenEditUtil = (util) => {
    setEditingUtilId(util.id);
    setUtilForm({ Utility_Type: util.Utility_Type || '', Provider: util.Provider || '', Account_Number: util.Account_Number || '', Due_Day: util.Due_Day != null ? String(util.Due_Day) : '' });
    setShowUtilModal(true);
  };

  const handleDeleteUtil = (utilId) => {
    askConfirm(
      { title: 'Delete Utility', message: 'Delete this utility?', confirmLabel: 'Delete', variant: 'danger' },
      async () => { try {
        await fetch(`${BASE_URL}/DeleteUtilities?utility_id=${utilId}`, { method: 'DELETE' });
        setUtilities(prev => prev.filter(u => u.id !== utilId));
      } catch {} }
    );
  };

  const handleOpenAddAsset = () => {
    setEditingAssetId(null);
    setAssetForm({ Asset: '', Manufacturer: '', Model_Number: '', Install_Date: '', Warranty_Expiration_Date: '' });
    setShowAssetModal(true);
  };

  const handleOpenEditAsset = (asset) => {
    setEditingAssetId(asset.id);
    setAssetForm({
      Asset: asset.Asset || '',
      Manufacturer: asset.Manufacturer || '',
      Model_Number: asset.Model_Number || '',
      Install_Date: toInputDate(asset.Install_Date),
      Warranty_Expiration_Date: toInputDate(asset.Warranty_Expiration_Date),
    });
    setShowAssetModal(true);
  };

  const handleDeleteAsset = (assetId) => {
    askConfirm(
      { title: 'Delete Asset', message: 'Delete this asset?', confirmLabel: 'Delete', variant: 'danger' },
      async () => { try {
        await fetch(`${BASE_URL}/DeleteDoorAssets?asset_id=${assetId}`, { method: 'DELETE' });
        setAssets(prev => prev.filter(a => a.id !== assetId));
      } catch {} }
    );
  };

  const handleSaveAsset = async () => {
    if (!assetForm.Asset.trim()) return;
    setAssetSaving(true);
    try {
      const params = new URLSearchParams({ Asset: assetForm.Asset });
      if (assetForm.Manufacturer) params.set('Manufacturer', assetForm.Manufacturer);
      if (assetForm.Model_Number) params.set('Model_Number', assetForm.Model_Number);
      if (assetForm.Install_Date) params.set('Install_Date', assetForm.Install_Date);
      if (assetForm.Warranty_Expiration_Date) params.set('Warranty_Expiration_Date', assetForm.Warranty_Expiration_Date);
      if (editingAssetId) {
        params.set('asset_id', editingAssetId);
        await fetch(`${BASE_URL}/EditDoorAssets?${params}`, { method: 'PUT' });
        setAssets(prev => prev.map(a => a.id === editingAssetId ? { ...a, ...assetForm } : a));
      } else {
        params.set('Door_id', getActiveDoorId());
        await fetch(`${BASE_URL}/AddDoorAssets?${params}`, { method: 'POST' });
        await fetchAssets(getActiveDoorId());
      }
      setShowAssetModal(false);
    } catch {}
    setAssetSaving(false);
  };

  const fetchPastTenants = async (doorId) => {
    if (!doorId) return;
    setPastTenantsLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/GetPastTenants?Door_id=${doorId}`);
      if (r.ok) { const d = await r.json(); setPastTenants(Array.isArray(d) ? d : (d.past_tenants || [])); }
    } catch {}
    setPastTenantsLoading(false);
  };

  const handleOpenEditPastTenant = (pt) => {
    setEditingPastTenantId(pt.id || pt.past_tenant_id);
    setPastTenantForm({
      Full_Name: pt.Full_Name || pt.full_name || '',
      Phone_Number: pt.Phone_Number || pt.phone_number || '',
      Email_Address: pt.Email_Address || pt.email_address || '',
    });
    setShowPastTenantModal(true);
  };

  const handleDeletePastTenant = (ptId) => {
    askConfirm(
      { title: 'Remove Past Tenant', message: 'Remove this past tenant record?', confirmLabel: 'Remove', variant: 'danger' },
      async () => { try {
        await fetch(`${BASE_URL}/DeletePastTenants?past_tenant_id=${ptId}`, { method: 'DELETE' });
        setPastTenants(prev => prev.filter(pt => (pt.id || pt.past_tenant_id) !== ptId));
      } catch {} }
    );
  };

  const handleSavePastTenant = async () => {
    if (!pastTenantForm.Full_Name.trim()) return;
    setPastTenantSaving(true);
    try {
      const params = new URLSearchParams({ Full_Name: pastTenantForm.Full_Name });
      if (pastTenantForm.Phone_Number) params.set('Phone_Number', pastTenantForm.Phone_Number);
      if (pastTenantForm.Email_Address) params.set('Email_Address', pastTenantForm.Email_Address);
      params.set('past_tenant_id', editingPastTenantId);
      await fetch(`${BASE_URL}/EditPastTenants?${params}`, { method: 'PUT' });
      setPastTenants(prev => prev.map(pt =>
        (pt.id || pt.past_tenant_id) === editingPastTenantId
          ? { ...pt, Full_Name: pastTenantForm.Full_Name, Phone_Number: pastTenantForm.Phone_Number, Email_Address: pastTenantForm.Email_Address }
          : pt
      ));
      setShowPastTenantModal(false);
    } catch {}
    setPastTenantSaving(false);
  };

  const handleOpenAddDoc = () => {
    setEditingDocId(null);
    setDocForm({ document_name: '', share_with_tenant: false, file: null });
    setShowDocModal(true);
  };

  const handleOpenEditDoc = (doc) => {
    setEditingDocId(doc.id);
    setDocForm({ document_name: doc.Document_Name || '', share_with_tenant: !!(doc.ShareWithTenant), file: null });
    setShowDocModal(true);
  };

  const handleDeleteDoc = (docId) => {
    askConfirm(
      { title: 'Delete Document', message: 'Delete this document?', confirmLabel: 'Delete', variant: 'danger' },
      async () => { try {
        await fetch(`${BASE_URL}/DeleteStoredDocs?document_id=${docId}`, { method: 'DELETE' });
        setStoredDocs(prev => prev.filter(d => d.id !== docId));
      } catch {} }
    );
  };

  const handleSaveDoc = async () => {
    if (!docForm.document_name.trim()) return;
    setDocSaving(true);
    try {
      const doorId = getActiveDoorId();
      if (editingDocId) {
        const params = new URLSearchParams({
          document_id: editingDocId,
          Document_Name: docForm.document_name,
          ShareWithTenant: docForm.share_with_tenant ? 1 : 0,
        });
        await fetch(`${BASE_URL}/EditStoredDocs?${params}`, { method: 'PUT' });
        setStoredDocs(prev => prev.map(d => d.id === editingDocId
          ? { ...d, Document_Name: docForm.document_name, ShareWithTenant: docForm.share_with_tenant ? 1 : 0 }
          : d));
      } else {
        if (!docForm.file) { setDocSaving(false); return; }
        const fd = new FormData();
        fd.append('file', docForm.file);
        const params = new URLSearchParams({
          door_id: doorId,
          document_name: docForm.document_name,
          share_with_tenant: (docForm.share_with_tenant && property?.tenantId) ? true : false,
          owner: 'Manager',
        });
        if (pmId) params.set('pm_id', pmId);
        await fetch(`${BASE_URL}/AddStoredDocs?${params}`, { method: 'POST', body: fd });
        await fetchStoredDocs(doorId);
      }
      setShowDocModal(false);
    } catch {}
    setDocSaving(false);
  };

  const handleSaveUtil = async () => {
    if (!utilForm.Utility_Type.trim() || !utilForm.Provider.trim()) return;
    setUtilSaving(true);
    try {
      const params = new URLSearchParams({ Utility_Type: utilForm.Utility_Type, Provider: utilForm.Provider });
      if (utilForm.Account_Number) params.set('Account_Number', utilForm.Account_Number);
      if (utilForm.Due_Day) params.set('Due_Day', parseInt(utilForm.Due_Day));
      if (editingUtilId) {
        params.set('utility_id', editingUtilId);
        await fetch(`${BASE_URL}/EditUtilities?${params}`, { method: 'PUT' });
        setUtilities(prev => prev.map(u => u.id === editingUtilId ? { ...u, Utility_Type: utilForm.Utility_Type, Provider: utilForm.Provider, Account_Number: utilForm.Account_Number, Due_Day: utilForm.Due_Day ? parseInt(utilForm.Due_Day) : null } : u));
      } else {
        params.set('Door_id', getActiveDoorId());
        await fetch(`${BASE_URL}/AddUtilities?${params}`, { method: 'POST' });
        await fetchUtilities(getActiveDoorId());
      }
      setShowUtilModal(false);
    } catch {}
    setUtilSaving(false);
  };

  if (loading) return <LoadingScreen />;
  if (!property) return <LoadingScreen message="Property not found" />;

  const isMultiFamily = property.propertyType === 'Multi Family Home';
  const unit = (doorFilter ? property.units.find(u => String(u.doorId) === String(doorFilter)) : property.units?.[0]) || property;
  const unitLabel = unit.unitNumber
    ? `Unit ${unit.unitNumber}`
    : (typeof unit.doorIndexInAll === 'number' ? `Unit ${String.fromCharCode(65 + unit.doorIndexInAll)}` : 'Unit');

  const fmtDate = (d) => {
    if (!d) return 'N/A';
    const s = String(d).split('T')[0]; const p = s.split('-');
    if (p.length === 3 && p[0].length === 4) return `${p[1]}/${p[2]}/${p[0]}`;
    return s;
  };
  const fmtDateShort = (d) => {
    if (!d) return '—';
    const s = String(d).split('T')[0]; const p = s.split('-');
    if (p.length === 3 && p[0].length === 4) {
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[parseInt(p[1])-1]} ${parseInt(p[2])}, ${p[0]}`;
    }
    return d;
  };
  const fmt$ = (v) => `$${Number(v||0).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})}`;

  const leaseEndRelative = (endDate) => {
    if (!endDate) return 'N/A';
    const p = endDate.split('-').map(Number);
    if (p.length !== 3) return 'N/A';
    const end   = new Date(p[0], p[1] - 1, p[2]);    // local midnight of end date
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((end - today) / 86400000);
    if (diffDays < 0)  return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
    const months = Math.round(diffDays / 30);
    return `${months} month${months === 1 ? '' : 's'}`;
  };

  const getNextRentDue = (rentDueDay) => {
    const day = parseInt(rentDueDay);
    if (!day || day < 1 || day > 31) return 'N/A';
    const now = new Date();
    let next = new Date(now.getFullYear(), now.getMonth(), day);
    if (next <= now) next = new Date(now.getFullYear(), now.getMonth() + 1, day);
    return next.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const now = new Date(); const cm = now.getMonth(); const cy = now.getFullYear();
  const parseMonthOf = (monthOf) => {
    if (!monthOf) return null;
    if (/^\d{4}-\d{2}/.test(monthOf)) return new Date(monthOf + 'T00:00:00');
    const parsed = new Date(monthOf.includes(' ') ? monthOf : `${monthOf} ${cy}`);
    return isNaN(parsed) ? null : parsed;
  };
  const matchThisMonth = (a) => {
    if (a.rawDate) {
      const d = new Date(a.rawDate);
      return d.getMonth() === cm && d.getFullYear() === cy;
    }
    const d = parseMonthOf(a.monthOf);
    return d ? d.getMonth() === cm && d.getFullYear() === cy : false;
  };
  const moneyIn      = activities.filter(a => a.amount > 0 && a.activityType !== 'rent_forgiveness' && matchThisMonth(a)).reduce((s,a)=>s+a.amount,0);
  const moneyOut     = activities.filter(a => a.amount < 0 && matchThisMonth(a)).reduce((s,a)=>s+Math.abs(a.amount),0);
  const forgivenAmt  = activities.filter(a => a.activityType === 'rent_forgiveness' && matchThisMonth(a)).reduce((s,a)=>s+a.amount,0);

  const mortgage      = parseFloat(property.mortgagePayment)||0;
  const hoa           = parseFloat(property.hoa)||0;
  const annualTaxes   = parseFloat(property.propertyTaxes)||0;
  const annualIns     = parseFloat(property.propertyInsurance)||0;
  const monthlyTax    = Math.floor(annualTaxes / 12);
  const monthlyIns    = Math.floor(annualIns / 12);
  const totalMonthly  = mortgage + hoa + monthlyTax + monthlyIns;

  const tenantInitials = unit.tenantName
    ? unit.tenantName.trim().split(/\s+/).map(n=>n[0]).join('').slice(0,2).toUpperCase() : '?';
  const isOccupied = unit.status === 'Occupied';

  const leaseProgressPct = (() => {
    if (!unit.leaseStartDate || !unit.leaseEndDate) return 0;
    const start = new Date(unit.leaseStartDate);
    const end   = new Date(unit.leaseEndDate);
    const total = end - start;
    if (total <= 0) return 100;
    return Math.min(100, Math.max(0, ((new Date() - start) / total) * 100));
  })();

  const woStatusColor = { Open: 'pd-wo-open', 'In Progress': 'pd-wo-inprogress', Completed: 'pd-wo-completed' };
  const woPriorityColor = { Urgent: 'pd-wo-pri-urgent', High: 'pd-wo-pri-high', Medium: 'pd-wo-pri-medium', Low: 'pd-wo-pri-low' };

  // ── TAB: OVERVIEW ────────────────────────────────────────────────
  const renderOverview = () => (
    <div className="pd-tab-body">
      <div className="pd-overview-grid">
        <div className="pd-left-col">
          {/* Hero card */}
          <div className="pd-hero-card">
            <div className="pd-hero-top">
              <div className="pd-hero-left">
                <span className="pd-hero-label">MONTHLY RENT</span>
                <div className="pd-hero-rent">
                  {fmt$(unit.rent)}<span className="pd-hero-mo">/mo</span>
                </div>
                <span className="pd-hero-sub">Due {getNextRentDue(unit.rentDueDate)}</span>
              </div>
              <div className="pd-hero-right">
                <span className="pd-hero-label">LEFT ON LEASE</span>
                <div className="pd-hero-lease">{leaseEndRelative(unit.leaseEndDate)}</div>
                <div className={`pd-hero-status-badge ${isOccupied ? 'pd-hero-badge-occ' : 'pd-hero-badge-vac'}`}>
                  <span className="pd-hero-status-dot" />
                  {unit.status || 'Vacant'}
                </div>
              </div>
            </div>
            <div className="pd-hero-dates">
              <span>Started {fmtDateShort(unit.leaseStartDate)}</span>
              <span>Ends {fmtDateShort(unit.leaseEndDate)}</span>
            </div>
            <div className="pd-hero-progress-bar">
              <div className="pd-hero-progress-fill" style={{ width: `${leaseProgressPct}%` }} />
            </div>
          </div>

          {/* Current Tenant */}
          <div className="pd-section-card">
          <div className="pd-section-hd">
            <h3>Current Tenant</h3>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              {isOccupied && isEditMode && editingSection !== 'rent' && (
                <button className="pd-icon-btn pd-icon-btn-blue" onClick={() => setEditingSection('rent')}><FaEdit /></button>
              )}
              {isOccupied && <button className="pd-others-btn" onClick={handleOthersClick}><FaUsers /> Others</button>}
            </div>
          </div>
          {editingSection === 'rent' ? (
            <div className="pd-edit-form">
              <div className="pd-ef"><label>Tenant Name</label>
                <input name="tenantName" value={editedProperty.tenantName||''} onChange={handleChange} className="pd-ei" />
              </div>
              <div className="pd-ef"><label>Phone Number</label>
                <input name="phoneNumber" value={editedProperty.phoneNumber||''} onChange={handleChange} className="pd-ei" placeholder="(555) 000-0000" />
              </div>
              <div className="pd-ef-row">
                <div className="pd-ef"><label>Monthly Rent</label>
                  <div className="pd-ei-dollar-wrap">
                    <span className="pd-ei-dollar">$</span>
                    <input type="number" name="rent" value={editedProperty.rent||''} onChange={handleChange} className="pd-ei pd-ei-dollar-input" />
                  </div>
                </div>
                <div className="pd-ef"><label>Security Deposit</label>
                  <div className="pd-ei-dollar-wrap">
                    <span className="pd-ei-dollar">$</span>
                    <input type="number" name="deposit" value={editedProperty.deposit||''} onChange={handleChange} className="pd-ei pd-ei-dollar-input" />
                  </div>
                </div>
              </div>
              <div className="pd-ef-row">
                <div className="pd-ef"><label>Lease Start</label>
                  <input type="date" name="leaseStartDate" value={editedProperty.leaseStartDate||''} onChange={handleChange} className="pd-ei" />
                </div>
                <div className="pd-ef"><label>Lease End</label>
                  <input type="date" name="leaseEndDate" value={editedProperty.leaseEndDate||''} onChange={handleChange} className="pd-ei" />
                </div>
              </div>
              <div className="pd-ef-row">
                <div className="pd-ef"><label>Rent Due Day</label>
                  <input type="number" name="rentDueDate" min="1" max="31" value={editedProperty.rentDueDate||''} onChange={handleChange} className="pd-ei" />
                </div>
                <div className="pd-ef"><label>Lease Type</label>
                  <select name="leaseType" value={editedProperty.leaseType||'Fixed'} onChange={handleChange} className="pd-ei">
                    <option value="Fixed">Fixed</option>
                    <option value="Month-to-Month">Month to Month</option>
                    <option value="Annual">Annual</option>
                  </select>
                </div>
              </div>
              <div className="pd-ef-actions">
                <button className="pd-cancel-sm" onClick={() => { setEditingSection(null); setEditedProperty(property); }}>Cancel</button>
                <button className="pd-save-sm" onClick={handleSave}>Save</button>
              </div>
            </div>
          ) : isOccupied && unit.tenantName ? (
            <div className="pd-tenant-body">
              <div className="pd-tenant-main-row">
                <div className="pd-tenant-avatar">{tenantInitials}</div>
                <div>
                  <div className="pd-tenant-name">{unit.tenantName}</div>
                  <div className="pd-tenant-movedin">Moved in {fmtDate(unit.leaseStartDate)}</div>
                </div>
              </div>
              <div className="pd-section-divider" />
              <div className="pd-contact-item">
                <div className="pd-contact-icon-wrap"><FaDollarSign /></div>
                <div className="pd-contact-detail">
                  <span className="pd-contact-lbl">Monthly Rent</span>
                  <span className="pd-contact-val pd-green-text">{fmt$(unit.rent)}</span>
                </div>
              </div>
              <div className="pd-contact-item">
                <div className="pd-contact-icon-wrap"><FaPhone /></div>
                <div className="pd-contact-detail">
                  <span className="pd-contact-lbl">Phone</span>
                  <span className="pd-contact-val pd-blue-text">{formatPhone(unit.phoneNumber) || '—'}</span>
                </div>
              </div>
              <div className="pd-contact-item">
                <div className="pd-contact-icon-wrap"><FaEnvelope /></div>
                <div className="pd-contact-detail">
                  <span className="pd-contact-lbl">Email</span>
                  <span className="pd-contact-val">—</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="pd-refcode-body">
              <div className="pd-vacant-placeholder" style={{ marginBottom: 14 }}>No current tenant — Vacant</div>
              {unit.referenceCode && (
                <>
                  <p className="pd-refcode-desc">Share this code with your tenant to connect their app to this unit.</p>
                  <div className="pd-refcode-row">
                    <span className="pd-refcode-value">{unit.referenceCode}</span>
                    <button
                      className={`pd-refcode-copy-btn ${copiedCode ? 'pd-refcode-copied' : ''}`}
                      onClick={() => {
                        navigator.clipboard.writeText(unit.referenceCode).catch(() => {});
                        setCopiedCode(true);
                        setTimeout(() => setCopiedCode(false), 2000);
                      }}
                    >
                      {copiedCode ? <FaCheckCircle /> : <FaCopy />}
                      {copiedCode ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        </div>{/* end pd-left-col */}

        <div className="pd-right-col">
          {/* Property Details */}
          <div className="pd-section-card">
            <div className="pd-section-hd">
              <h3>Property Details</h3>
              {isEditMode && editingSection !== 'property' && <button className="pd-icon-btn pd-icon-btn-blue" onClick={() => setEditingSection('property')}><FaEdit /></button>}
            </div>
            {editingSection === 'property' ? (
              <div className="pd-edit-form">
                <div className="pd-ef"><label>Property Name</label><input name="name" value={editedProperty.name||''} onChange={handleChange} className="pd-ei" /></div>
                <div className="pd-ef"><label>Street Address</label><input name="streetAddress" value={editedProperty.streetAddress||''} onChange={handleChange} className="pd-ei" /></div>
                <div className="pd-ef-row">
                  <div className="pd-ef"><label>City</label><input name="city" value={editedProperty.city||''} onChange={handleChange} className="pd-ei" /></div>
                  <div className="pd-ef"><label>State</label><input name="state" value={editedProperty.state||''} onChange={handleChange} className="pd-ei" /></div>
                </div>
                <div className="pd-ef"><label>Zip Code</label><input name="zipcode" value={editedProperty.zipcode||''} onChange={handleChange} className="pd-ei" /></div>
                <div className="pd-ef-actions">
                  <button className="pd-cancel-sm" onClick={() => { setEditingSection(null); setEditedProperty(property); }}>Cancel</button>
                  <button className="pd-save-sm" onClick={handleSave}>Save</button>
                </div>
              </div>
            ) : (
              <div className="pd-detail-rows">
                <div className="pd-detail-item"><span className="pd-dl">Property Name</span><span className="pd-dv">{property.name}</span></div>
                <div className="pd-section-divider" />
                <div className="pd-detail-item"><span className="pd-dl">Street Address</span><span className="pd-dv">{(doorFilter && unit.streetAddress ? unit.streetAddress : property.streetAddress) || '—'}</span></div>
                <div className="pd-detail-item"><span className="pd-dl">City</span><span className="pd-dv">{(doorFilter && unit.city ? unit.city : property.city)||'—'}</span></div>
                <div className="pd-detail-item"><span className="pd-dl">Zip Code</span><span className="pd-dv">{(doorFilter && unit.zipcode ? unit.zipcode : property.zipcode)||'—'}</span></div>
                <div className="pd-detail-item"><span className="pd-dl">State</span><span className="pd-dv">{(doorFilter && unit.state ? unit.state : property.state)||'—'}</span></div>
              </div>
            )}
          </div>

          {/* Important Dates */}
          <div className="pd-section-card">
            <div className="pd-section-hd">
              <h3>Important Dates</h3>
              {isEditMode && editingSection !== 'dates' && <button className="pd-icon-btn pd-icon-btn-blue" onClick={() => setEditingSection('dates')}><FaEdit /></button>}
            </div>
            {editingSection === 'dates' ? (
              <div className="pd-edit-form">
                <div className="pd-ef-row">
                  <div className="pd-ef"><label>Lease Start</label><input type="date" name="leaseStartDate" value={editedProperty.leaseStartDate||''} onChange={handleChange} className="pd-ei" /></div>
                  <div className="pd-ef"><label>Lease End</label><input type="date" name="leaseEndDate" value={editedProperty.leaseEndDate||''} onChange={handleChange} className="pd-ei" /></div>
                </div>
                <div className="pd-ef-row">
                  <div className="pd-ef"><label>Rent Due Day</label><input type="number" name="rentDueDate" min="1" max="31" value={editedProperty.rentDueDate||''} onChange={handleChange} className="pd-ei" /></div>
                  <div className="pd-ef"><label>Lease Type</label>
                    <select name="leaseType" value={editedProperty.leaseType||'Fixed'} onChange={handleChange} className="pd-ei">
                      <option value="Fixed">Fixed</option>
                      <option value="Month-to-Month">Month to Month</option>
                      <option value="Annual">Annual</option>
                    </select>
                  </div>
                </div>
                <div className="pd-ef-row">
                  <div className="pd-ef"><label>Insurance Start</label><input type="date" name="insuranceStartDate" value={editedProperty.insuranceStartDate||''} onChange={handleChange} className="pd-ei" /></div>
                  <div className="pd-ef"><label>Insurance End</label><input type="date" name="insuranceEndDate" value={editedProperty.insuranceEndDate||''} onChange={handleChange} className="pd-ei" /></div>
                </div>
                <div className="pd-ef-actions">
                  <button className="pd-cancel-sm" onClick={() => { setEditingSection(null); setEditedProperty(property); }}>Cancel</button>
                  <button className="pd-save-sm" onClick={handleSave}>Save</button>
                </div>
              </div>
            ) : (
              <div className="pd-dates-list">
                <div className="pd-date-row pd-ins-row">
                  <div className="pd-date-icon-wrap"><FaCalendarAlt /></div>
                  <span className="pd-date-lbl">Lease Period</span>
                  <div className="pd-ins-chips">
                    <div className="pd-ins-chip"><span className="pd-ins-chip-lbl">START</span><span className="pd-ins-chip-val">{fmtDateShort(unit.leaseStartDate)}</span></div>
                    <span className="pd-ins-arrow">→</span>
                    <div className="pd-ins-chip"><span className="pd-ins-chip-lbl">END</span><span className="pd-ins-chip-val">{fmtDateShort(unit.leaseEndDate)}</span></div>
                  </div>
                </div>
                <div className="pd-date-row pd-ins-row">
                  <div className="pd-date-icon-wrap"><FaShieldAlt /></div>
                  <span className="pd-date-lbl">Insurance</span>
                  <div className="pd-ins-chips">
                    <div className="pd-ins-chip"><span className="pd-ins-chip-lbl">START</span><span className="pd-ins-chip-val">{fmtDateShort(property.insuranceStartDate)}</span></div>
                    <span className="pd-ins-arrow">→</span>
                    <div className="pd-ins-chip"><span className="pd-ins-chip-lbl">END</span><span className="pd-ins-chip-val">{fmtDateShort(property.insuranceEndDate)}</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>{/* end pd-right-col */}

      </div>{/* end pd-overview-grid */}

      {/* Reference Code */}
      {unit.referenceCode && isOccupied && (
        <div className="pd-overview-grid">
        <div className="pd-section-card">
          <div className="pd-section-hd">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h3>Reference Code</h3>
              <FaInfoCircle className="pd-refcode-info-icon" title="Share this code with your tenant to connect their app to this unit." />
            </div>
          </div>
          <div className="pd-refcode-body">
            <p className="pd-refcode-desc">Share this code with your tenant to connect their app to this unit.</p>
            <div className="pd-refcode-row">
              <span className="pd-refcode-value">{unit.referenceCode}</span>
              <button
                className={`pd-refcode-copy-btn ${copiedCode ? 'pd-refcode-copied' : ''}`}
                onClick={() => {
                  navigator.clipboard.writeText(unit.referenceCode).catch(() => {});
                  setCopiedCode(true);
                  setTimeout(() => setCopiedCode(false), 2000);
                }}
              >
                {copiedCode ? <FaCheckCircle /> : <FaCopy />}
                {copiedCode ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Multi-family units overview */}
      {isMultiFamily && !doorFilter && (
        <div className="pd-section-card">
          <div className="pd-section-hd"><h3>Units</h3></div>
          <div className="pd-units-grid">
            {(property.units || []).map((u, i) => (
              <div key={u.doorId || i} className="pd-unit-mini" onClick={() => navigate(`/properties/${id}?door=${u.doorId}`)}>
                <div className="pd-unit-mini-hd"><span>Unit {u.unitNumber || i+1}</span><span className={`pd-unit-mini-badge ${u.status==='Occupied'?'pd-st-occ':'pd-st-vac'}`}>{u.status}</span></div>
                <span className="pd-unit-mini-rent">{fmt$(u.rent)}/mo</span>
                {u.tenantName && <span className="pd-unit-mini-tenant">{u.tenantName}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Danger zone — only visible in edit mode */}
      {isEditMode && (
        <div className="pd-danger-row">
          <button className="pd-delete-prop-btn" onClick={handleDelete}><FaTrash /> Delete Property</button>
        </div>
      )}
    </div>
  );

  // ── TAB: ACTIVITY ────────────────────────────────────────────────
  const renderActivity = () => (
    <div className="pd-tab-body">
      {/* Money In / Out / Forgiven */}
      <div className="pd-money-row">
        <div className="pd-money-card pd-money-in">
          <div className="pd-money-icon-wrap pd-money-icon-green"><FaArrowUp /></div>
          <div className="pd-money-text">
            <div className="pd-money-main-row">
              <span className="pd-money-lbl">Money In</span>
              <span className="pd-money-val pd-green-text">${moneyIn.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
              <span className="pd-money-sub">This month</span>
            </div>
          </div>
        </div>
        <div className="pd-money-card pd-money-out">
          <div className="pd-money-icon-wrap pd-money-icon-red"><FaArrowDown /></div>
          <div className="pd-money-text">
            <div className="pd-money-main-row">
              <span className="pd-money-lbl">Money Out</span>
              <span className="pd-money-val pd-red-text">${moneyOut.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
              <span className="pd-money-sub">This month</span>
            </div>
          </div>
        </div>
        {forgivenAmt > 0 && (
          <div className="pd-money-card pd-money-forgiven">
            <div className="pd-money-icon-wrap pd-money-icon-yellow"><FaTag /></div>
            <div className="pd-money-text">
              <div className="pd-money-main-row">
                <span className="pd-money-lbl">Forgiven</span>
                <span className="pd-money-val pd-yellow-text">${forgivenAmt.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                <span className="pd-money-sub">This month</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Past Due Rent */}
      <div className={`pd-pastdue-card ${pastDueAmount > 0 ? 'pd-pastdue-alert' : ''}`}>
        <div className={`pd-pastdue-icon ${pastDueAmount > 0 ? 'pd-pastdue-icon-red' : ''}`}>
          {pastDueAmount > 0 ? <FaExclamationTriangle /> : <FaCheckCircle />}
        </div>
        <div>
          <span className="pd-pastdue-lbl">Past Due Rent</span>
          {pastDueAmount === null ? (
            <span className="pd-pastdue-val" style={{ color: '#9ca3af' }}>Loading…</span>
          ) : pastDueAmount > 0 ? (
            <>
              <span className="pd-pastdue-val pd-red-text">
                -${pastDueAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="pd-pastdue-sub" style={{ color: '#dc2626' }}>Past due</span>
            </>
          ) : (
            <>
              <span className="pd-pastdue-val pd-green-text">$0.00</span>
              <span className="pd-pastdue-sub">All caught up</span>
            </>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="pd-section-card">
        <div className="pd-section-hd">
          <h3>Recent Activity</h3>
          <div className="pd-act-btns" ref={actMenuRef}>
            <button
              className={`pd-act-filter-btn ${actFilterType !== 'all' || actFilterPeriod !== 'all' ? 'pd-act-filter-active' : ''}`}
              title="Filter"
              onClick={() => setShowActFilter(true)}
            ><FaFilter /></button>
            <div style={{ position: 'relative' }}>
              <button className="pd-act-add-btn" onClick={() => setShowAddActMenu(v => !v)}><FaPlus /></button>
              {showAddActMenu && (
                <div className="pd-act-add-menu">
                  <button className="pd-act-add-menu-item" onClick={() => { setAddActType('income'); setShowAddActMenu(false); resetIncForm(); }}>
                    <FaArrowUp style={{ color: '#16a34a', marginRight: 8 }} />Add Income
                  </button>
                  <button className="pd-act-add-menu-item" onClick={() => { setAddActType('expense'); setShowAddActMenu(false); setAddExpForm({ title: '', amount: '', date: new Date().toISOString().split('T')[0], category: '', utilityType: '', notes: '', doorId: '' }); }}>
                    <FaArrowDown style={{ color: '#dc2626', marginRight: 8 }} />Add Expense
                  </button>
                  <button className="pd-act-add-menu-item" onClick={() => { setAddActType('fee'); setShowAddActMenu(false); setFeeForm({ feeId: '', selectedFee: null, notes: '', doorId: '', tenantId: null }); }}>
                    <FaBolt style={{ color: '#2563eb', marginRight: 8 }} />Push Fee
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {activitiesLoading ? (
          <div className="pd-empty-state">Loading…</div>
        ) : getFilteredActivities().length === 0 ? (
          <div className="pd-empty-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 20px', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaReceipt style={{ fontSize: 20, color: '#9ca3af' }} />
            </div>
            <p style={{ margin: 0, fontWeight: 600, color: '#374151', fontSize: 14 }}>No activities yet</p>
            <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>Use the + button to add income, expenses, or fees</p>
          </div>
        ) : (
          <div className="pd-act-list">
            {getFilteredActivities().map(act => {
              const isForgiv = act.activityType === 'rent_forgiveness';
              const isPF = act.activityType === 'pushed_fee';
              const iconClass = isForgiv ? 'pd-act-icon-yellow' : isPF ? 'pd-act-icon-blue' : act.amount > 0 ? 'pd-act-icon-green' : 'pd-act-icon-red';
              const amtClass = isForgiv ? 'pd-yellow-text' : isPF ? 'pd-blue-text' : act.amount > 0 ? 'pd-green-text' : 'pd-red-text';
              return (
                <div key={act.id} className="pd-act-item pd-act-item-clickable" onClick={() => setSelectedAct(act)}>
                  <div className={`pd-act-icon-wrap ${iconClass}`}>
                    {isForgiv ? <FaTag /> : isPF ? <FaBolt /> : act.amount > 0 ? <FaArrowUp /> : <FaArrowDown />}
                  </div>
                  <div className="pd-act-info">
                    <div className="pd-act-type-row">
                      <span className="pd-act-type">{act.type}</span>
                      {isForgiv && <span className="pd-act-badge pd-act-badge-forgiveness">Forgiveness</span>}
                      {isPF && <span className="pd-act-badge pd-act-badge-pushed-fee">{act.status === 'paid' ? 'Paid' : 'Pending'}</span>}
                      {!isForgiv && !isPF && act.isPartial && <span className="pd-act-badge pd-act-badge-partial">Partial Payment</span>}
                    </div>
                    <span className="pd-act-date">{act.date || act.monthOf}</span>
                    {act.notes && <span className="pd-act-notes">{act.notes}</span>}
                  </div>
                  <span className={`pd-act-amount ${amtClass}`}>
                    {isForgiv ? '' : act.amount < 0 ? '-' : '+'}${Math.abs(act.amount).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transaction Detail Popup */}
      {selectedAct && (
        <div className="rd-modal-overlay" onClick={() => setSelectedAct(null)}>
          <div className="rd-modal" onClick={e => e.stopPropagation()}>
            <button className="rd-modal-close" onClick={() => setSelectedAct(null)}><FaTimes /></button>
            {(() => {
              const isFG = selectedAct.activityType === 'rent_forgiveness';
              const isPF = selectedAct.activityType === 'pushed_fee';
              const isRent = selectedAct.activityType === 'rent';
              const isFeeIncome = isRent && selectedAct.category === 'Fee';
              const modalTitle = isFG ? 'Forgiveness Details' : isPF ? 'Fee Details' : isFeeIncome ? 'Fee Details' : isRent ? 'Income Details' : 'Expense Details';
              return <h2 className="rd-modal-title">{modalTitle}</h2>;
            })()}

            {(() => {
              const isFG = selectedAct.activityType === 'rent_forgiveness';
              const isPF = selectedAct.activityType === 'pushed_fee';
              const isRent = selectedAct.activityType === 'rent';
              const isFeeIncome = isRent && selectedAct.category === 'Fee';
              const heroClass = isFG ? 'rd-modal-hero-yellow' : isPF ? 'rd-modal-hero-blue' : selectedAct.amount > 0 ? 'rd-modal-hero-green' : 'rd-modal-hero-red';
              const valClass = isFG ? 'rd-mhv-yellow' : isPF ? 'rd-mhv-blue' : selectedAct.amount > 0 ? 'rd-mhv-green' : 'rd-mhv-red';
              const label = isFG ? 'Rent Forgiveness' : isPF ? (selectedAct.type || 'Pushed Fee') : isFeeIncome ? 'Fee' : (selectedAct.type || (selectedAct.amount > 0 ? 'Income' : 'Expense'));
              const isMultiUnit = parseInt(property?.numberOfUnits || 0) > 1;
              const heroSub = selectedAct.vendor || ((isMultiUnit && selectedAct.unitNumber) ? selectedAct.unitNumber : (selectedAct.date || selectedAct.monthOf));
              return (
                <div className={`rd-modal-hero ${heroClass}`}>
                  <div className="rd-modal-hero-label">{label}</div>
                  {isPF && selectedAct.status && (
                    <span className={`rd-hero-fee-status ${selectedAct.status === 'paid' ? 'rd-fee-paid' : 'rd-fee-pending'}`}>
                      {selectedAct.status === 'paid' ? '✓ Paid' : '⏳ Pending'}
                    </span>
                  )}
                  <div className={`rd-modal-hero-val ${valClass}`}>
                    {isFG ? '' : selectedAct.amount > 0 ? '+' : '−'}${Math.abs(selectedAct.amount).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                  </div>
                  {heroSub && <div className="rd-modal-hero-sub">{heroSub}</div>}
                  {selectedAct.isPartial && (
                    <div className="rd-hero-badges">
                      <span className="rd-hero-badge rd-hero-badge-partial">Partial Payment</span>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="rd-modal-section-label">Details</div>
            <div className="rd-modal-rows">
              <div className="rd-mrow">
                <span className="rd-mrow-icon rd-mri-blue"><FaBuilding /></span>
                <span className="rd-mrow-label">Property</span>
                <span className="rd-mrow-val">
                  {property?.name || '—'}
                  {selectedAct.unitNumber && parseInt(property?.numberOfUnits || 0) > 1
                    ? ` / ${selectedAct.unitNumber}` : ''}
                </span>
              </div>
              <div className="rd-mrow">
                <span className="rd-mrow-icon rd-mri-orange"><FaCalendarAlt /></span>
                <span className="rd-mrow-label">Date</span>
                <span className="rd-mrow-val">{fmtDateShort(selectedAct.rawDate || selectedAct.date)}</span>
              </div>
              {selectedAct.monthOf && <>
                <div className="rd-modal-divider" />
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-orange"><FaCalendarAlt /></span>
                  <span className="rd-mrow-label">Month Of</span>
                  <span className="rd-mrow-val">{selectedAct.monthOf}</span>
                </div>
              </>}
              {selectedAct.isPartial && <>
                <div className="rd-modal-divider" />
                <div className="rd-mrow">
                  <span className="rd-mrow-icon" style={{ color: '#d97706' }}><FaTag /></span>
                  <span className="rd-mrow-label">Payment Status</span>
                  <span className="rd-mrow-val" style={{ color: '#92400e', fontWeight: 700 }}>Partial Payment</span>
                </div>
              </>}
              {selectedAct.notes && <>
                <div className="rd-modal-divider" />
                <div className="rd-mrow">
                  <span className="rd-mrow-icon rd-mri-gray"><FaStickyNote /></span>
                  <span className="rd-mrow-label">Notes</span>
                  <span className="rd-mrow-val">{selectedAct.notes}</span>
                </div>
              </>}
            </div>

            <div className="rd-modal-divider" style={{ marginTop: 18 }} />
            <div className="txn-modal-actions">
              <div>
                {(selectedAct.activityType === 'rent' || selectedAct.activityType === 'expense' || selectedAct.activityType === 'rent_forgiveness') && !selectedAct.isPastDue && (
                  <button className="txn-modal-edit-btn" onClick={() => handlePDEditActivity(selectedAct)}>
                    <FaEdit /> Edit
                  </button>
                )}
              </div>
              <button className="txn-modal-close-btn" onClick={() => setSelectedAct(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── TAB: WORK ORDERS ─────────────────────────────────────────────
  const renderWorkOrders = () => (
    <div className="pd-tab-body">
      <div className="pd-section-card">
        <div className="pd-section-hd">
          <h3>Work Orders</h3>
          <button className="pd-act-add-btn" onClick={() => setShowAddWO(true)}><FaPlus /></button>
        </div>
        {workOrdersLoading ? (
          <div className="pd-empty-state">Loading…</div>
        ) : workOrders.length === 0 ? (
          <div className="pd-empty-state">No work orders yet.</div>
        ) : (
          <div className="pd-wo-list">
            {workOrders.map((wo, i) => (
                <div
                  key={wo.ID || i}
                  className="pd-wo-card"
                  style={{cursor:'pointer'}}
                  onClick={() => openWODrawer(wo)}
                >
                  <div className="pd-wo-header">
                    <span className={`pd-wo-status ${woStatusColor[wo.Status] || 'pd-wo-open'}`}>{wo.Status || 'Open'}</span>
                    <span className={`pd-wo-priority ${woPriorityColor[wo.Priority] || 'pd-wo-pri-medium'}`}>{wo.Priority || 'Medium'}</span>
                  </div>
                  {wo.Property_Name && (
                    <div className="pd-wo-property-name">{wo.Property_Name}</div>
                  )}
                  <div className="pd-wo-title">{wo.Title}</div>
                  <div className="pd-wo-footer">
                    <div className="pd-wo-due"><FaCalendarAlt className="pd-wo-cal" /> Due {fmtDateShort(wo.Due_Date)}</div>
                    <span className="pd-wo-view-link">VIEW DETAILS →</span>
                  </div>
                </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── TAB: FINANCIAL ───────────────────────────────────────────────
  const isEditingFin = isEditMode;

  // Inline editable value: shows input in edit mode, formatted value otherwise
  const finVal = (displayVal, name, type = 'number') => isEditingFin ? (
    <div className="pd-fin-inline-input-wrap">
      {type === 'number' && <span className="pd-fin-inline-dollar">$</span>}
      <input
        className="pd-fin-inline-input"
        type={type}
        name={name}
        value={editedProperty[name] ?? ''}
        onChange={handleChange}
        placeholder="0"
      />
    </div>
  ) : (
    <span className="pd-fin-val">{displayVal}</span>
  );

  const renderFinancial = () => (
    <div className="pd-tab-body">
      {/* Save / Cancel bar — only visible while global edit mode is on */}
      {isEditingFin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 4 }}>
          <button className="pd-cancel-sm" onClick={() => { setIsEditMode(false); setEditingSection(null); setEditedProperty(property); }}>Cancel</button>
          <button className="pd-save-changes-btn" style={{ margin: 0 }} onClick={async () => { await handleSave(); setIsEditMode(false); }}>Save Changes</button>
        </div>
      )}

      {/* Lease Financials */}
      <div className="pd-section-card">
        <div className="pd-section-hd"><h3>Lease Financials</h3></div>
        <div className="pd-fin-list">
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#d1fae5',color:'#059669'}}><FaDollarSign /></div>
            <span className="pd-fin-lbl">Monthly Rent</span>
            {!isMultiFamily ? finVal(fmt$(unit.rent), 'rent') : <span className="pd-fin-val">{fmt$(unit.rent)}</span>}
          </div>
          <div className="pd-section-divider" />
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#ede9fe',color:'#7c3aed'}}><FaShieldAlt /></div>
            <span className="pd-fin-lbl">Security Deposit</span>
            {!isMultiFamily ? finVal(fmt$(unit.deposit), 'deposit') : <span className="pd-fin-val">{fmt$(unit.deposit)}</span>}
          </div>
        </div>
      </div>

      {/* Investment */}
      <div className="pd-section-card">
        <div className="pd-section-hd"><h3>Investment</h3></div>
        <div className="pd-fin-list">
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#dbeafe',color:'#2563eb'}}><FaHome /></div>
            <span className="pd-fin-lbl">Purchase Price</span>
            {finVal(fmt$(property.purchasePrice), 'purchasePrice')}
          </div>
          <div className="pd-section-divider" />
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#dcfce7',color:'#16a34a'}}><FaDollarSign /></div>
            <span className="pd-fin-lbl">Down Payment</span>
            {finVal(fmt$(property.downPayment), 'downPayment')}
          </div>
          <div className="pd-section-divider" />
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#fed7aa',color:'#c2410c'}}><FaHammer /></div>
            <span className="pd-fin-lbl">Renovation</span>
            {finVal(fmt$(property.renovCost), 'renovCost')}
          </div>
          <div className="pd-section-divider" />
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#f3e8ff',color:'#9333ea'}}><FaDollarSign /></div>
            <span className="pd-fin-lbl">Total Cost</span>
            <span className="pd-fin-val">{fmt$((parseFloat(property.purchasePrice)||0) + (parseFloat(property.renovCost)||0))}</span>
          </div>
        </div>
      </div>

      {/* Monthly Expenses */}
      <div className="pd-section-card">
        <div className="pd-section-hd"><h3>Monthly Expenses</h3></div>
        <div className="pd-fin-list">
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#dbeafe',color:'#2563eb'}}><FaHome /></div>
            <span className="pd-fin-lbl">Mortgage</span>
            {finVal(fmt$(mortgage), 'mortgagePayment')}
          </div>
          <div className="pd-section-divider" />
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#ede9fe',color:'#7c3aed'}}><FaCity /></div>
            <span className="pd-fin-lbl">HOA Fees</span>
            {finVal(fmt$(hoa), 'hoa')}
          </div>
          <div className="pd-section-divider" />
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#fef3c7',color:'#d97706'}}><FaDollarSign /></div>
            <div className="pd-fin-lbl-wrap">
              <span className="pd-fin-lbl">Annual Taxes</span>
              {!isEditingFin && annualTaxes > 0 && <span className="pd-fin-sublbl">{fmt$(annualTaxes)}/yr</span>}
            </div>
            {isEditingFin
              ? finVal(fmt$(monthlyTax * 12), 'propertyTaxes')
              : <span className="pd-fin-val">{fmt$(monthlyTax)}</span>}
          </div>
          <div className="pd-section-divider" />
          <div className="pd-fin-item">
            <div className="pd-fin-icon" style={{background:'#d1fae5',color:'#059669'}}><FaShieldAlt /></div>
            <div className="pd-fin-lbl-wrap">
              <span className="pd-fin-lbl">Annual Insurance</span>
              {!isEditingFin && annualIns > 0 && <span className="pd-fin-sublbl">{fmt$(annualIns)}/yr</span>}
            </div>
            {isEditingFin
              ? finVal(fmt$(monthlyIns * 12), 'propertyInsurance')
              : <span className="pd-fin-val">{fmt$(monthlyIns)}</span>}
          </div>
          {isEditingFin && (
            <>
              <div className="pd-section-divider" />
              <div className="pd-fin-item">
                <div className="pd-fin-icon" style={{background:'#f0f9ff',color:'#0ea5e9'}}><FaCalendarAlt /></div>
                <span className="pd-fin-lbl">Insurance Start</span>
                {finVal('', 'insuranceStartDate', 'date')}
              </div>
              <div className="pd-section-divider" />
              <div className="pd-fin-item">
                <div className="pd-fin-icon" style={{background:'#f0f9ff',color:'#0ea5e9'}}><FaCalendarAlt /></div>
                <span className="pd-fin-lbl">Insurance End</span>
                {finVal('', 'insuranceEndDate', 'date')}
              </div>
            </>
          )}
          <div className="pd-section-divider" />
          <div className="pd-fin-item pd-fin-total">
            <span className="pd-fin-total-lbl">Total Monthly</span>
            <span className="pd-fin-total-val pd-blue-text">{fmt$(totalMonthly)}/mo</span>
          </div>
        </div>
      </div>

      {/* Additional Fees */}
      {additionalFees.length > 0 && (
        <div className="pd-section-card">
          <div className="pd-section-hd"><h3>Additional Fees</h3></div>
          <div className="pd-fin-list">
            {additionalFees.map((fee, i) => (
              <div key={i}>
                {i > 0 && <div className="pd-section-divider" />}
                <div className="pd-fin-item">
                  <div className="pd-fin-icon" style={{background:'#eff6ff',color:'#2563eb'}}><FaDollarSign /></div>
                  <span className="pd-fin-lbl">{fee.Type || fee.fee_name || fee.name || 'Fee'}</span>
                  <span className="pd-fin-val">{fmt$(fee.Amount ?? fee.fee_amount ?? fee.amount ?? 0)}/mo</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ── MODALS ───────────────────────────────────────────────────────

  // Filter drawer (right-side, same structure as Activity.js)
  const renderFilterDrawer = () => (
    <>
      {showActFilter && <div className="filter-drawer-backdrop" onClick={() => setShowActFilter(false)} />}
      <div className={`filter-drawer ${showActFilter ? 'filter-drawer-open' : ''}`}>
        <div className="filter-header">
          <h3>FILTERS</h3>
          <button className="modal-close-btn" onClick={() => setShowActFilter(false)}><FaTimes /></button>
        </div>
        <div className="filter-body">
          <div className="filter-section">
            <div className="filter-section-header">
              <span className="filter-section-title">Transaction Type</span>
            </div>
            {[['all','All'],['income','Income'],['expense','Expense'],['forgiveness','Forgiveness']].map(([v,l]) => (
              <div key={v} className="filter-toggle-row" onClick={() => setActFilterType(v)}
                style={{ background: actFilterType === v ? '#eff6ff' : '' }}>
                <span className="filter-toggle-label" style={{ color: actFilterType === v ? '#2563eb' : '' }}>{l}</span>
                {actFilterType === v && <span style={{ color: '#2563eb', fontSize: 14 }}>✓</span>}
              </div>
            ))}
          </div>
          <div className="filter-section">
            <div className="filter-section-header">
              <span className="filter-section-title">Period</span>
            </div>
            {[['all','All Time'],['month','This Month'],['last_month','Last Month'],['year','This Year']].map(([v,l]) => (
              <div key={v} className="filter-toggle-row" onClick={() => setActFilterPeriod(v)}
                style={{ background: actFilterPeriod === v ? '#eff6ff' : '' }}>
                <span className="filter-toggle-label" style={{ color: actFilterPeriod === v ? '#2563eb' : '' }}>{l}</span>
                {actFilterPeriod === v && <span style={{ color: '#2563eb', fontSize: 14 }}>✓</span>}
              </div>
            ))}
          </div>
          {(actFilterType !== 'all' || actFilterPeriod !== 'all') && (
            <div style={{ padding: '12px 16px' }}>
              <button
                onClick={() => { setActFilterType('all'); setActFilterPeriod('all'); }}
                style={{ width: '100%', padding: '10px', border: '1.5px solid #e5e7eb', borderRadius: 10, background: '#fff', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Add Income modal (Activity.js style)
  const renderAddIncomeModal = () => addActType === 'income' && (
    <div className="modal-overlay" onClick={() => setAddActType(null)}>
      <div className="new-activity-modal modal-income" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{editingIncId ? 'Edit Income' : 'Add Income'}</h3>
          <button className="modal-close-btn" onClick={() => setAddActType(null)}><FaTimes /></button>
        </div>
        <div className="modal-body">
          {/* Unit selector for multi-unit without doorFilter */}
          {isMultiFamily && !doorFilter && (property.units || []).length > 1 && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>Unit *</label>
                <select value={addIncForm.doorId} onChange={e => setAddIncForm(p => ({ ...p, doorId: e.target.value, tenantId: null }))}>
                  <option value="">Select Unit</option>
                  {(property.units || []).map(u => (
                    <option key={u.doorId} value={u.doorId}>{u.unitNumber || `Unit ${u.doorId}`}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Category *</label>
              <select value={addIncForm.category} onChange={e => setAddIncForm(p => ({...p, category: e.target.value, tenantId: null}))}>
                <option value="Rent">Rent</option>
                <option value="Fee">Fee</option>
                <option value="Rent Forgiveness">Rent Forgiveness</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                {addIncForm.category === 'Rent Forgiveness' ? 'Forgiveness Amount *' : 'Payment Amount *'}
                {addIncForm.category === 'Rent' && expectedRent !== null && addIncForm.amount && (
                  parseFloat(addIncForm.amount) < expectedRent
                    ? <span style={{ fontWeight: 400, color: '#d97706', fontSize: 12, marginLeft: 6 }}>Partial payment</span>
                    : <span style={{ fontWeight: 400, color: '#16a34a', fontSize: 12, marginLeft: 6 }}>auto-filled</span>
                )}
              </label>
              <input type="number" value={addIncForm.amount} onChange={e => setAddIncForm(p => ({...p, amount: e.target.value}))} placeholder="0.00" />
              {addIncForm.category === 'Rent' && expectedRent !== null && parseFloat(addIncForm.amount || 0) < expectedRent && (
                <div style={{ fontSize: 12, color: '#d97706', marginTop: 4 }}>
                  Rent due: ${expectedRent.toLocaleString('en-US', { minimumFractionDigits: 2 })} — this will be marked as a partial payment
                </div>
              )}
            </div>
          </div>
          {/* Rent: month of */}
          {addIncForm.category === 'Rent' && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>Month Of *</label>
                <input type="month" value={addIncForm.month} onChange={e => setAddIncForm(p => ({...p, month: e.target.value}))} className="act-month-input" />
              </div>
            </div>
          )}
          {/* Rent Forgiveness: forgiveness month + tenant status */}
          {addIncForm.category === 'Rent Forgiveness' && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>Month Of (Forgiveness) *</label>
                <input type="month" value={addIncForm.forgivenessMonth} onChange={e => setAddIncForm(p => ({...p, forgivenessMonth: e.target.value}))} className="act-month-input" />
                {forgivenessResolveError
                  ? <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#dc2626' }}>Vacant — rent forgiveness is not available.</div>
                  : addIncForm.tenantId
                    ? <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#16a34a' }}>Occupied ✓</div>
                    : <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>Resolving tenant…</div>
                }
              </div>
            </div>
          )}
          {/* Regular income: date + method */}
          {addIncForm.category !== 'Rent Forgiveness' && (
            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={addIncForm.date} onChange={e => setAddIncForm(p => ({...p, date: e.target.value}))} />
              </div>
              <div className="form-group">
                <label>Payment Method *</label>
                <select value={addIncForm.method} onChange={e => setAddIncForm(p => ({...p, method: e.target.value}))}>
                  <option value="">Select Method</option>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Zelle">Zelle</option>
                  <option value="Venmo">Venmo</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          )}
          <div className="form-row single">
            <div className="form-group">
              <label>Notes</label>
              <textarea value={addIncForm.notes} onChange={e => setAddIncForm(p => ({...p, notes: e.target.value}))} placeholder="Add any additional notes..." />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <div />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-cancel" onClick={() => setAddActType(null)}>Cancel</button>
            <button className="btn-save" onClick={handlePDSaveIncome} disabled={addActSaving || (addIncForm.category === 'Rent Forgiveness' && forgivenessResolveError)}>
              {addActSaving ? 'Saving…' : editingIncId ? 'Save Changes' : addIncForm.category === 'Rent Forgiveness' ? 'Save Forgiveness' : 'Save Income'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Add Expense modal (Activity.js style)
  const renderAddExpenseModal = () => addActType === 'expense' && (
    <div className="modal-overlay" onClick={() => setAddActType(null)}>
      <div className="new-activity-modal modal-expense" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{editingExpId ? 'Edit Expense' : 'Add Expense'}</h3>
          <button className="modal-close-btn" onClick={() => setAddActType(null)}><FaTimes /></button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input type="text" value={addExpForm.title} onChange={e => setAddExpForm(p => ({...p, title: e.target.value}))} placeholder="Expense title" />
            </div>
            <div className="form-group">
              <label>Amount *</label>
              <input type="number" value={addExpForm.amount} onChange={e => setAddExpForm(p => ({...p, amount: e.target.value}))} placeholder="0.00" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Pay Date *</label>
              <input type="date" value={addExpForm.date} onChange={e => setAddExpForm(p => ({...p, date: e.target.value}))} />
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select value={addExpForm.category} onChange={e => setAddExpForm(p => ({...p, category: e.target.value, utilityType: '' }))}>
                <option value="">Select Category</option>
                <option value="Repair">Repair</option>
                <option value="Operation Expense">Operation Expense</option>
                <option value="Utility">Utility</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Renovation">Renovation</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          {addExpForm.category === 'Operation Expense' && (
            <div className="utility-type-box">
              <div className="form-group">
                <label>Type *</label>
                <select value={addExpForm.utilityType} onChange={e => setAddExpForm(p => ({...p, utilityType: e.target.value}))}>
                  <option value="">Select Type</option>
                  <option value="General">General</option>
                  <option value="HOA">HOA</option>
                  <option value="Tax">Tax</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Mortgage">Mortgage</option>
                </select>
              </div>
            </div>
          )}
          {addExpForm.category === 'Utility' && (
            <div className="utility-type-box">
              <div className="form-group">
                <label>Type *</label>
                <select value={addExpForm.utilityType} onChange={e => setAddExpForm(p => ({...p, utilityType: e.target.value}))}>
                  <option value="">Select Type</option>
                  <option value="General">General</option>
                  <option value="Water">Water</option>
                  <option value="Gas">Gas</option>
                  <option value="Electricity">Electricity</option>
                  <option value="Trash">Trash</option>
                  <option value="Sewer">Sewer</option>
                </select>
              </div>
            </div>
          )}
          <div className="form-row single">
            <div className="form-group">
              <label>Notes</label>
              <textarea value={addExpForm.notes} onChange={e => setAddExpForm(p => ({...p, notes: e.target.value}))} placeholder="Add any additional notes..." />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <div />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-cancel" onClick={() => setAddActType(null)}>Cancel</button>
            <button className="btn-save btn-expense" onClick={handlePDSaveExpense} disabled={addActSaving}>{addActSaving ? 'Saving…' : editingExpId ? 'Save Changes' : 'Save Expense'}</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPushFeeModal = () => addActType === 'fee' && (
    <div className="modal-overlay" onClick={() => setAddActType(null)}>
      <div className="new-activity-modal modal-fee" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title"><FaBolt style={{ color: '#2563eb', marginRight: 8 }} />Push Fee to Tenant</h2>
          <button className="modal-close-btn" onClick={() => setAddActType(null)}><FaTimes /></button>
        </div>
        <div className="modal-body">
          {/* Unit selector for multi-unit */}
          {isMultiFamily && !doorFilter && (property.units || []).length > 1 && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>Unit *</label>
                <select value={feeForm.doorId} onChange={e => setFeeForm(p => ({ ...p, doorId: e.target.value, feeId: '', selectedFee: null, tenantId: null }))}>
                  <option value="">Select Unit</option>
                  {(property.units || []).map(u => (
                    <option key={u.doorId} value={u.doorId}>{u.unitNumber || `Unit ${u.doorId}`}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {feeFormLoading && <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Loading fees…</div>}
          {!feeFormLoading && availableFees.length > 0 && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>Fee *</label>
                <select value={feeForm.feeId} onChange={e => {
                  const fee = availableFees.find(f => String(f.id || f.Fee_Id) === e.target.value);
                  setFeeForm(p => ({ ...p, feeId: e.target.value, selectedFee: fee || null }));
                }}>
                  <option value="">Select Fee</option>
                  {availableFees.map(f => {
                    const fid = f.id || f.Fee_Id;
                    const ftype = f.Type || f.type || f.Fee_Type || f.fee_name || f.name || 'Fee';
                    const famt = f.Amount || f.amount || 0;
                    return <option key={fid} value={fid}>{ftype} — ${Number(famt).toFixed(2)}</option>;
                  })}
                </select>
                {feeForm.selectedFee && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#374151' }}>
                    Amount: <strong>${Number(feeForm.selectedFee.Amount || feeForm.selectedFee.amount || 0).toFixed(2)}</strong>
                  </div>
                )}
              </div>
            </div>
          )}
          {!feeFormLoading && (feeForm.doorId || unit?.doorId) && availableFees.length === 0 && (
            <div style={{ fontSize: 13, color: '#d97706', marginBottom: 8 }}>No fees configured for this unit's lease.</div>
          )}
          <div className="form-row single">
            <div className="form-group">
              <label>Notes</label>
              <textarea value={feeForm.notes} onChange={e => setFeeForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes…" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <div />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-cancel" onClick={() => setAddActType(null)}>Cancel</button>
            <button className="btn-save" onClick={handlePDSaveFee} disabled={addActSaving || !feeForm.feeId}>
              {addActSaving ? 'Pushing…' : 'Push Fee'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOthersModal = () => showOthers && (
    <div className="pd-modal-overlay" onClick={() => setShowOthers(false)}>
      <div className="pd-modal" onClick={e => e.stopPropagation()}>
        <div className="pd-modal-hd">
          <button className="pd-modal-done" onClick={() => setShowOthers(false)}>Done</button>
          <h3>Other Occupants</h3>
          <button className="pd-modal-plus" onClick={() => { setShowOthers(false); setShowAddOcc(true); }}><FaPlus /></button>
        </div>
        {otherOccs.length === 0 ? (
          <div className="pd-occ-empty">
            <FaUsers className="pd-occ-empty-icon" />
            <p>No additional occupants</p>
            <span>Click + to add someone who lives here</span>
          </div>
        ) : (
          <div className="pd-occ-list">
            {otherOccs.map((occ, i) => (
              <div key={i} className="pd-occ-item">
                <div className="pd-tenant-avatar" style={{width:36,height:36,fontSize:13}}>{(occ.full_name||occ.Full_Name||'?').slice(0,1).toUpperCase()}</div>
                <div>
                  <div className="pd-occ-name">{occ.full_name || occ.Full_Name}</div>
                  <div className="pd-occ-phone">{formatPhone(occ.phone_number || occ.Phone_Number) || '—'}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderAddOccModal = () => showAddOcc && (
    <div className="pd-modal-overlay" onClick={() => setShowAddOcc(false)}>
      <div className="pd-modal" onClick={e => e.stopPropagation()}>
        <div className="pd-modal-hd">
          <button className="pd-modal-done" onClick={() => setShowAddOcc(false)}>Cancel</button>
          <h3>Add Occupant</h3>
          <button className="pd-modal-done" style={{color:'#2563eb'}} onClick={submitAddOcc} disabled={occSubmitting}>Save</button>
        </div>
        <div className="pd-modal-body">
          <p className="pd-modal-section-lbl">New Occupant</p>
          <div className="pd-modal-card-inputs">
            <input className="pd-modal-input" placeholder="Full Name" value={addOccForm.fullName} onChange={e => setAddOccForm(p => ({...p, fullName: e.target.value}))} />
            <div className="pd-modal-input-div" />
            <input className="pd-modal-input" placeholder="Phone Number" value={addOccForm.phone} onChange={e => setAddOccForm(p => ({...p, phone: formatPhone(e.target.value)}))} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderAddWOModal = () => showAddWO && (
    <div className="modal-overlay" onClick={() => setShowAddWO(false)}>
      <div className="new-activity-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Work Order</h3>
          <button className="modal-close-btn" onClick={() => setShowAddWO(false)}><FaTimes /></button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input type="text" placeholder="e.g. Fix leaking faucet" value={woForm.title} onChange={e => setWoForm(p=>({...p,title:e.target.value}))} />
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select value={woForm.priority} onChange={e => setWoForm(p=>({...p,priority:e.target.value}))}>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group full-width">
              <label>Due Date *</label>
              <input type="date" value={woForm.dueDate} onChange={e => setWoForm(p=>({...p,dueDate:e.target.value}))} />
            </div>
          </div>
          {property.tenantName && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>Tenant</label>
                <input type="text" value={`${property.tenantName}${property.phoneNumber ? ' · ' + property.phoneNumber : ''}`} readOnly style={{ color: '#6b7280', background: '#f9fafb' }} />
              </div>
            </div>
          )}
          <div className="form-row single">
            <div className="form-group">
              <label>Description *</label>
              <textarea placeholder="Describe the issue in detail..." value={woForm.description} onChange={e => setWoForm(p=>({...p,description:e.target.value}))} maxLength={250} />
              <span style={{ fontSize: 11, color: '#9ca3af', float: 'right' }}>{woForm.description.length}/250</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <div />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-cancel" onClick={() => setShowAddWO(false)}>Cancel</button>
            <button className="btn-save" onClick={submitWorkOrder} disabled={woSubmitting}>{woSubmitting ? 'Saving…' : 'Create Work Order'}</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWODrawer = () => {
    const wo = selectedWO;
    const statusColors = { Open: '#2563eb', 'In Progress': '#d97706', Completed: '#16a34a' };
    const priorityColors = { Urgent: '#dc2626', High: '#ea580c', Medium: '#d97706', Low: '#6b7280' };
    return (
      <>
        {wo && <div className="filter-drawer-backdrop" onClick={() => setSelectedWO(null)} />}
        <div className={`filter-drawer pd-wo-drawer ${wo ? 'filter-drawer-open' : ''}`}>
          {wo && (
            <>
              <div className="filter-header">
                <h3>Work Order</h3>
                <button className="modal-close-btn" onClick={() => setSelectedWO(null)}><FaTimes /></button>
              </div>
              <div className="filter-body" style={{ padding: '16px' }}>
                {/* Status + Priority */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: `${statusColors[wo.Status] || '#2563eb'}18`, color: statusColors[wo.Status] || '#2563eb' }}>{wo.Status || 'Open'}</span>
                  <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: `${priorityColors[wo.Priority] || '#d97706'}18`, color: priorityColors[wo.Priority] || '#d97706' }}>{wo.Priority || 'Medium'}</span>
                </div>

                {/* Title */}
                <h2 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 16px' }}>{wo.Title}</h2>

                {/* Info rows */}
                <div className="pd-wo-info-rows">
                  {wo.Unit_Number && (
                    <div className="pd-wo-info-row">
                      <span className="pd-wo-info-lbl">Unit</span>
                      <span className="pd-wo-info-val">{wo.Unit_Number}</span>
                    </div>
                  )}
                  <div className="pd-wo-info-row">
                    <span className="pd-wo-info-lbl">Submitted</span>
                    <span className="pd-wo-info-val">{wo.Date_Submitted ? new Date(wo.Date_Submitted).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</span>
                  </div>
                  <div className="pd-wo-info-row">
                    <span className="pd-wo-info-lbl">Due Date</span>
                    <span className="pd-wo-info-val">{wo.Due_Date ? new Date(wo.Due_Date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</span>
                  </div>
                </div>

                {/* Description */}
                {wo.Description && (
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Description</p>
                    <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0, background: '#f9fafb', borderRadius: 10, padding: '10px 12px' }}>{wo.Description}</p>
                  </div>
                )}

                {/* Timeline */}
                <div style={{ marginTop: 20 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 12px' }}>Timeline</p>
                  {woDrawerLoading ? (
                    <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>
                  ) : woDrawerTimeline.length === 0 ? (
                    <p style={{ color: '#9ca3af', fontSize: 13 }}>No timeline entries yet.</p>
                  ) : (
                    <div className="pd-wo-timeline">
                      {woDrawerTimeline.map((t, i) => (
                        <div key={i} className="pd-wo-tl-item">
                          <div className="pd-wo-tl-dot" />
                          <div className="pd-wo-tl-body">
                            <p className="pd-wo-tl-title">{t.Title || t.title || t.update || ''}</p>
                            <p className="pd-wo-tl-meta">{t._author || t.Updated_By || t.updated_by || ''}{t.Timestamp || t.timestamp ? ` · ${new Date(t.Timestamp || t.timestamp).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}` : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Open full details link */}
                <button
                  className="btn-save"
                  style={{ width: '100%', marginTop: 24 }}
                  onClick={() => navigate(`/work-orders/${wo.ID}`, { state: { workOrder: wo } })}
                >
                  Open Full Details
                </button>
              </div>
            </>
          )}
        </div>
      </>
    );
  };

  // ── TAB: INFO ────────────────────────────────────────────────────
  const ordinalDay = (n) => { if (!n) return ''; const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10]||s[v]||s[0]); };

  const renderInfo = () => (
    <div className="pd-tab-body" style={{display:'flex',flexDirection:'column',gap:16}}>
      <div className="pd-section-card">
        {/* Header — always visible, toggles expand/collapse */}
        <div className="pd-section-hd" style={{cursor:'pointer'}} onClick={() => setUtilExpanded(v => !v)}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div className="pd-util-hd-icon"><FaBolt /></div>
            <div>
              <h3 style={{margin:0}}>Utilities</h3>
              {utilities.length > 0 && <span style={{fontSize:12,color:'#6b7280'}}>{utilities.map(u=>u.Utility_Type).join(' · ')}</span>}
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            {isEditMode && utilExpanded && (
              <button
                className="pd-icon-btn"
                style={{color:'#2563eb'}}
                onClick={e => { e.stopPropagation(); handleOpenAddUtil(); }}
              ><FaPlus /></button>
            )}
            {utilExpanded ? <FaChevronUp style={{color:'#9ca3af',fontSize:13}} /> : <FaChevronDown style={{color:'#9ca3af',fontSize:13}} />}
          </div>
        </div>

        {utilExpanded && (
          utilitiesLoading ? (
            <div className="pd-empty-state">Loading…</div>
          ) : utilities.length === 0 ? (
            <div className="pd-util-empty-wrap">
              <button className="pd-util-add-btn" onClick={e => { e.stopPropagation(); handleOpenAddUtil(); }}><FaPlus /> Add a Provider</button>
            </div>
          ) : (
            <>
              {utilities.map((util, i) => (
                <div key={util.id || i}>
                  {i > 0 && <div className="pd-section-divider" />}
                  <div className="pd-util-entry">
                    <div className="pd-util-entry-hd">
                      <div>
                        <div className="pd-util-type">{util.Utility_Type}</div>
                        <div className="pd-util-provider">{util.Provider}</div>
                      </div>
                      {isEditMode && (
                        <div style={{display:'flex',gap:6}}>
                          <button className="pd-icon-btn pd-icon-btn-blue" onClick={() => handleOpenEditUtil(util)}><FaEdit /></button>
                          <button className="pd-icon-btn pd-icon-btn-red" onClick={() => handleDeleteUtil(util.id)}><FaTrash /></button>
                        </div>
                      )}
                    </div>
                    {util.Account_Number ? <div className="pd-util-row"><span className="pd-util-lbl">Account #</span><span className="pd-util-val">{util.Account_Number}</span></div> : null}
                    {util.Due_Day ? <div className="pd-util-row"><span className="pd-util-lbl">Due Day</span><span className="pd-util-val">{ordinalDay(util.Due_Day)} of each month</span></div> : null}
                  </div>
                </div>
              ))}
              <div className="pd-util-add-more-wrap">
                <button className="pd-util-add-more-btn" onClick={handleOpenAddUtil}><FaPlus /> Add Another Provider</button>
              </div>
            </>
          )
        )}
      </div>
      {renderAssets()}
      {renderStoredDocs()}
      {renderPastTenants()}
    </div>
  );

  const renderAssets = () => (
    <div className="pd-section-card">
      <div className="pd-section-hd" style={{cursor:'pointer'}} onClick={() => setAssetsExpanded(v => !v)}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div className="pd-asset-hd-icon"><FaTools /></div>
          <div>
            <h3 style={{margin:0}}>Door Assets</h3>
            {assets.length > 0
              ? <span style={{fontSize:12,color:'#6b7280'}}>{assets.map(a=>a.Asset).join(' · ')}</span>
              : <span style={{fontSize:12,color:'#9ca3af'}}>No assets recorded</span>}
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {isEditMode && assetsExpanded && (
            <button className="pd-icon-btn" style={{color:'#2563eb'}} onClick={e => { e.stopPropagation(); handleOpenAddAsset(); }}><FaPlus /></button>
          )}
          {assetsExpanded ? <FaChevronUp style={{color:'#9ca3af',fontSize:13}} /> : <FaChevronDown style={{color:'#9ca3af',fontSize:13}} />}
        </div>
      </div>

      {assetsExpanded && (
        assetsLoading ? (
          <div className="pd-empty-state">Loading…</div>
        ) : assets.length === 0 ? (
          <div className="pd-util-empty-wrap">
            <button className="pd-util-add-btn" onClick={e => { e.stopPropagation(); handleOpenAddAsset(); }}><FaPlus /> Add an Asset</button>
          </div>
        ) : (
          <>
            {assets.map((asset, i) => (
              <div key={asset.id || i}>
                {i > 0 && <div className="pd-section-divider" />}
                <div className="pd-util-entry">
                  <div className="pd-util-entry-hd">
                    <div>
                      <div className="pd-util-type">{asset.Asset}</div>
                      {asset.Manufacturer && <div className="pd-util-provider">{asset.Manufacturer}</div>}
                    </div>
                    {isEditMode && (
                      <div style={{display:'flex',gap:6}}>
                        <button className="pd-icon-btn pd-icon-btn-blue" onClick={() => handleOpenEditAsset(asset)}><FaEdit /></button>
                        <button className="pd-icon-btn pd-icon-btn-red" onClick={() => handleDeleteAsset(asset.id)}><FaTrash /></button>
                      </div>
                    )}
                  </div>
                  {asset.Model_Number && <div className="pd-util-row"><span className="pd-util-lbl">Model #</span><span className="pd-util-val">{asset.Model_Number}</span></div>}
                  {asset.Install_Date && <div className="pd-util-row"><span className="pd-util-lbl">Installed</span><span className="pd-util-val">{fmtDateShort(asset.Install_Date)}</span></div>}
                  {asset.Warranty_Expiration_Date && <div className="pd-util-row"><span className="pd-util-lbl">Warranty Exp.</span><span className="pd-util-val">{fmtDateShort(asset.Warranty_Expiration_Date)}</span></div>}
                </div>
              </div>
            ))}
            <div className="pd-util-add-more-wrap">
              <button className="pd-util-add-more-btn" onClick={handleOpenAddAsset}><FaPlus /> Add Another Asset</button>
            </div>
          </>
        )
      )}
    </div>
  );

  const renderPastTenants = () => (
    <div className="pd-section-card">
      <div className="pd-section-hd" style={{cursor:'pointer'}} onClick={() => setPastTenantsExpanded(v => !v)}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div className="pd-past-hd-icon"><FaUsers /></div>
          <div>
            <h3 style={{margin:0}}>Past Tenants</h3>
            {pastTenants.length > 0
              ? <span style={{fontSize:12,color:'#6b7280'}}>{pastTenants.length} past tenant{pastTenants.length !== 1 ? 's' : ''}</span>
              : <span style={{fontSize:12,color:'#9ca3af'}}>No past tenants</span>}
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {pastTenantsExpanded ? <FaChevronUp style={{color:'#9ca3af',fontSize:13}} /> : <FaChevronDown style={{color:'#9ca3af',fontSize:13}} />}
        </div>
      </div>

      {pastTenantsExpanded && (
        pastTenantsLoading ? (
          <div className="pd-empty-state">Loading…</div>
        ) : pastTenants.length === 0 ? (
          <div className="pd-util-empty-wrap">
            <p style={{margin:0,fontSize:14,color:'#9ca3af'}}>No past tenant records</p>
          </div>
        ) : (
          <>
            {pastTenants.map((pt, i) => {
              const ptId = pt.id || pt.past_tenant_id;
              const name = pt.Full_Name || pt.full_name || '—';
              const phone = formatPhone(pt.Phone_Number || pt.phone_number || '');
              const email = pt.Email_Address || pt.email_address || '';
              return (
                <div key={ptId || i}>
                  {i > 0 && <div className="pd-section-divider" />}
                  <div className="pd-util-entry">
                    <div className="pd-util-entry-hd">
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div className="pd-past-avatar">{name.charAt(0).toUpperCase()}</div>
                        <div>
                          <div className="pd-util-type">{name}</div>
                          {phone && <div className="pd-util-provider">{phone}</div>}
                        </div>
                      </div>
                      {isEditMode && (
                        <div style={{display:'flex',gap:6}}>
                          <button className="pd-icon-btn pd-icon-btn-blue" onClick={() => handleOpenEditPastTenant(pt)}><FaEdit /></button>
                          <button className="pd-icon-btn pd-icon-btn-red" onClick={() => handleDeletePastTenant(ptId)}><FaTrash /></button>
                        </div>
                      )}
                    </div>
                    {email && <div className="pd-util-row"><span className="pd-util-lbl">Email</span><span className="pd-util-val">{email}</span></div>}
                  </div>
                </div>
              );
            })}
          </>
        )
      )}
    </div>
  );

  const renderPastTenantModal = () => showPastTenantModal && (
    <div className="pd-util-modal-overlay" onClick={() => setShowPastTenantModal(false)}>
      <div className="pd-util-modal-card" onClick={e => e.stopPropagation()}>
        <div className="pd-util-modal-hd">
          <h3>Edit Past Tenant</h3>
          <button className="pd-util-modal-close" onClick={() => setShowPastTenantModal(false)}><FaTimes /></button>
        </div>
        <div className="pd-util-modal-body">
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Full Name <span style={{color:'#dc2626'}}>*</span></label>
            <input className="pd-util-modal-input" placeholder="Full name" value={pastTenantForm.Full_Name} onChange={e => setPastTenantForm(p=>({...p,Full_Name:e.target.value}))} />
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Phone Number <span style={{color:'#9ca3af',fontWeight:400}}>(optional)</span></label>
            <input className="pd-util-modal-input" placeholder="e.g. (555) 123-4567" value={pastTenantForm.Phone_Number} onChange={e => setPastTenantForm(p=>({...p,Phone_Number:formatPhone(e.target.value)}))} />
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Email Address <span style={{color:'#9ca3af',fontWeight:400}}>(optional)</span></label>
            <input className="pd-util-modal-input" type="email" placeholder="email@example.com" value={pastTenantForm.Email_Address} onChange={e => setPastTenantForm(p=>({...p,Email_Address:e.target.value}))} />
          </div>
        </div>
        <div className="pd-util-modal-footer">
          <button className="pd-util-modal-cancel" onClick={() => setShowPastTenantModal(false)}>Cancel</button>
          <button className="pd-util-modal-save" onClick={handleSavePastTenant} disabled={pastTenantSaving || !pastTenantForm.Full_Name.trim()}>
            {pastTenantSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  const openStoredDoc = async (docId) => {
    try {
      const res = await fetch(`${BASE_URL}/ViewStoredDocs?document_id=${docId}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {}
  };

  const renderStoredDocs = () => (
    <div className="pd-section-card">
      <div className="pd-section-hd" style={{cursor:'pointer'}} onClick={() => setDocsExpanded(v => !v)}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div className="pd-docs-hd-icon"><FaInfoCircle /></div>
          <div>
            <h3 style={{margin:0}}>Stored Documents</h3>
            {storedDocs.length > 0
              ? <span style={{fontSize:12,color:'#6b7280'}}>{storedDocs.length} document{storedDocs.length !== 1 ? 's' : ''}</span>
              : <span style={{fontSize:12,color:'#9ca3af'}}>No documents stored</span>}
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {isEditMode && docsExpanded && (
            <button className="pd-icon-btn" style={{color:'#2563eb'}} onClick={e => { e.stopPropagation(); handleOpenAddDoc(); }}><FaPlus /></button>
          )}
          {docsExpanded ? <FaChevronUp style={{color:'#9ca3af',fontSize:13}} /> : <FaChevronDown style={{color:'#9ca3af',fontSize:13}} />}
        </div>
      </div>

      {docsExpanded && (
        docsLoading ? (
          <div className="pd-empty-state">Loading…</div>
        ) : storedDocs.length === 0 ? (
          <div className="pd-util-empty-wrap">
            <button className="pd-util-add-btn" onClick={e => { e.stopPropagation(); handleOpenAddDoc(); }}><FaPlus /> Upload a Document</button>
          </div>
        ) : (
          <>
            {storedDocs.map((doc, i) => (
              <div key={doc.id || i}>
                {i > 0 && <div className="pd-section-divider" />}
                <div className="pd-util-entry">
                  <div className="pd-util-entry-hd">
                    <div style={{display:'flex',alignItems:'center',gap:8,flex:1,minWidth:0}}>
                      <div className="pd-doc-file-icon"><FaStickyNote /></div>
                      <div style={{minWidth:0}}>
                        <button className="pd-doc-name-link" onClick={() => openStoredDoc(doc.id)}>
                          {doc.Document_Name || 'Document'}
                        </button>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginTop:3}}>
                          {doc.ShareWithTenant === 1 && <span className="pd-doc-shared-badge">Shared with Tenant</span>}
                          {doc.Owner === 'Tenant' && <span className="pd-doc-owner-badge">Uploaded by Tenant</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                      {doc.Upload_Date && <span style={{fontSize:12,color:'#9ca3af',whiteSpace:'nowrap'}}>Uploaded {fmtDateShort(doc.Upload_Date)}</span>}
                      {isEditMode && doc.Owner === 'Manager' && (
                        <>
                          <button className="pd-icon-btn pd-icon-btn-blue" onClick={() => handleOpenEditDoc(doc)}><FaEdit /></button>
                          <button className="pd-icon-btn pd-icon-btn-red" onClick={() => handleDeleteDoc(doc.id)}><FaTrash /></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="pd-util-add-more-wrap">
              <button className="pd-util-add-more-btn" onClick={handleOpenAddDoc}><FaPlus /> Upload Another Document</button>
            </div>
          </>
        )
      )}
    </div>
  );

  const renderDocModal = () => showDocModal && (
    <div className="pd-util-modal-overlay" onClick={() => setShowDocModal(false)}>
      <div className="pd-util-modal-card" onClick={e => e.stopPropagation()}>
        <div className="pd-util-modal-hd">
          <h3>{editingDocId ? 'Edit Document' : 'Upload Document'}</h3>
          <button className="pd-util-modal-close" onClick={() => setShowDocModal(false)}><FaTimes /></button>
        </div>
        <div className="pd-util-modal-body">
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Document Name <span style={{color:'#dc2626'}}>*</span></label>
            <input className="pd-util-modal-input" placeholder="e.g. Lease Agreement, Inspection Report" value={docForm.document_name} onChange={e => setDocForm(p=>({...p,document_name:e.target.value}))} />
          </div>
          {!editingDocId && (
            <div className="pd-util-modal-group">
              <label className="pd-util-modal-lbl">File <span style={{color:'#dc2626'}}>*</span></label>
              <input type="file" className="pd-util-modal-input" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setDocForm(p=>({...p,file:e.target.files[0]||null}))} />
            </div>
          )}
          <div className="pd-util-modal-group">
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <label className="pd-util-modal-lbl" style={{marginBottom:0}}>Share with Tenant</label>
                {!property?.tenantId && (
                  <p style={{margin:'2px 0 0',fontSize:11,color:'#9ca3af'}}>No tenant set up for this unit</p>
                )}
              </div>
              <div
                className={`pd-doc-toggle ${docForm.share_with_tenant && property?.tenantId ? 'pd-doc-toggle-on' : ''} ${!property?.tenantId ? 'pd-doc-toggle-disabled' : ''}`}
                onClick={() => { if (property?.tenantId) setDocForm(p=>({...p,share_with_tenant:!p.share_with_tenant})); }}
                style={!property?.tenantId ? {opacity:0.4,cursor:'not-allowed'} : {}}
              >
                <div className="pd-doc-toggle-thumb" />
              </div>
            </div>
          </div>
        </div>
        <div className="pd-util-modal-footer">
          <button className="pd-util-modal-cancel" onClick={() => setShowDocModal(false)}>Cancel</button>
          <button className="pd-util-modal-save" onClick={handleSaveDoc} disabled={docSaving || !docForm.document_name.trim() || (!editingDocId && !docForm.file)}>
            {docSaving ? 'Saving…' : editingDocId ? 'Save' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderAssetModal = () => showAssetModal && (
    <div className="pd-util-modal-overlay" onClick={() => setShowAssetModal(false)}>
      <div className="pd-util-modal-card" onClick={e => e.stopPropagation()}>
        <div className="pd-util-modal-hd">
          <h3>{editingAssetId ? 'Edit Asset' : 'Add Asset'}</h3>
          <button className="pd-util-modal-close" onClick={() => setShowAssetModal(false)}><FaTimes /></button>
        </div>
        <div className="pd-util-modal-body">
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Asset Name <span style={{color:'#dc2626'}}>*</span></label>
            <input className="pd-util-modal-input" placeholder="e.g. Furnace, AC Unit, Water Heater" value={assetForm.Asset} onChange={e => setAssetForm(p=>({...p,Asset:e.target.value}))} />
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Manufacturer <span style={{color:'#9ca3af',fontWeight:400}}>(optional)</span></label>
            <input className="pd-util-modal-input" placeholder="e.g. Carrier, Rheem" value={assetForm.Manufacturer} onChange={e => setAssetForm(p=>({...p,Manufacturer:e.target.value}))} />
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Model Number <span style={{color:'#9ca3af',fontWeight:400}}>(optional)</span></label>
            <input className="pd-util-modal-input" placeholder="Model #" value={assetForm.Model_Number} onChange={e => setAssetForm(p=>({...p,Model_Number:e.target.value}))} />
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Install Date <span style={{color:'#9ca3af',fontWeight:400}}>(optional)</span></label>
            <input type="date" className="pd-util-modal-input" value={assetForm.Install_Date} onChange={e => setAssetForm(p=>({...p,Install_Date:e.target.value}))} />
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Warranty Expiration <span style={{color:'#9ca3af',fontWeight:400}}>(optional)</span></label>
            <input type="date" className="pd-util-modal-input" value={assetForm.Warranty_Expiration_Date} onChange={e => setAssetForm(p=>({...p,Warranty_Expiration_Date:e.target.value}))} />
          </div>
        </div>
        <div className="pd-util-modal-footer">
          <button className="pd-util-modal-cancel" onClick={() => setShowAssetModal(false)}>Cancel</button>
          <button className="pd-util-modal-save" onClick={handleSaveAsset} disabled={assetSaving || !assetForm.Asset.trim()}>
            {assetSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderUtilModal = () => showUtilModal && (
    <div className="pd-util-modal-overlay" onClick={() => setShowUtilModal(false)}>
      <div className="pd-util-modal-card" onClick={e => e.stopPropagation()}>
        <div className="pd-util-modal-hd">
          <h3>{editingUtilId ? 'Edit Utility' : 'Add Utility'}</h3>
          <button className="pd-util-modal-close" onClick={() => setShowUtilModal(false)}><FaTimes /></button>
        </div>
        <div className="pd-util-modal-body">
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Type <span style={{color:'#dc2626'}}>*</span></label>
            <select className="pd-util-modal-select" value={utilForm.Utility_Type} onChange={e => setUtilForm(p=>({...p,Utility_Type:e.target.value}))}>
              <option value="">Select type…</option>
              {['Electric','Gas','Water','Internet','Trash','Sewer','Other'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Provider <span style={{color:'#dc2626'}}>*</span></label>
            <input className="pd-util-modal-input" placeholder="e.g. AES, Columbia Gas" value={utilForm.Provider} onChange={e => setUtilForm(p=>({...p,Provider:e.target.value}))} />
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Account Number <span style={{color:'#9ca3af',fontWeight:400}}>(optional)</span></label>
            <input className="pd-util-modal-input" placeholder="Account number" value={utilForm.Account_Number} onChange={e => setUtilForm(p=>({...p,Account_Number:e.target.value}))} />
          </div>
          <div className="pd-util-modal-group">
            <label className="pd-util-modal-lbl">Due Day <span style={{color:'#9ca3af',fontWeight:400}}>(optional, 1–31)</span></label>
            <input className="pd-util-modal-input" type="number" min="1" max="31" placeholder="e.g. 5" value={utilForm.Due_Day} onChange={e => setUtilForm(p=>({...p,Due_Day:e.target.value}))} />
          </div>
        </div>
        <div className="pd-util-modal-footer">
          <button className="pd-util-modal-cancel" onClick={() => setShowUtilModal(false)}>Cancel</button>
          <button className="pd-util-modal-save" onClick={handleSaveUtil} disabled={utilSaving || !utilForm.Utility_Type || !utilForm.Provider.trim()}>
            {utilSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'activity', label: 'Activity' },
    { key: 'workorders', label: 'Work Orders' },
    { key: 'financial', label: 'Financial' },
    { key: 'info', label: 'Info' },
  ];

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title={property.name} />
        <div className="pd-container">
          {/* Breadcrumb */}
          <div className="pd-breadcrumb">
            <button className="pd-back-btn" onClick={() => navigate('/properties')}>← Properties</button>
            {doorFilter && <>
              <span className="pd-bc-sep">/</span>
              <span className="pd-bc-cur">{unit.unitNumber || unitLabel}</span>
            </>}
          </div>

          {/* Header bar */}
          <div className="pd-page-header">
            <div className="pd-page-header-left">
              <div className={`pd-type-icon ${isMultiFamily ? 'pd-type-mf' : 'pd-type-sf'}`}>
                {isMultiFamily ? <FaCity /> : <FaHome />}
              </div>
              <div>
                <div className="pd-page-title-row">
                  <h1 className="pd-page-title">{property.name}</h1>
                  {doorFilter && <span className="pd-unit-label-badge">{unitLabel}</span>}
                </div>
                <p className="pd-page-addr">{(() => {
                  const addr = (doorFilter && unit.streetAddress) ? unit : property;
                  return addr.streetAddress ? `${addr.streetAddress}, ${addr.city}, ${addr.state} ${addr.zipcode}` : property.address;
                })()}</p>
              </div>
            </div>
            <div className="pd-header-right">
              <span className={`pd-status-badge ${unit.status === 'Occupied' ? 'pd-st-occ' : unit.status === 'Partial' ? 'pd-st-partial' : 'pd-st-vac'}`}>{unit.status || 'Vacant'}</span>
              {activeTab !== 'activity' && activeTab !== 'workorders' && (
                <button className={`pd-edit-mode-btn ${isEditMode ? 'pd-edit-mode-active' : ''}`} onClick={async () => {
                  if (isEditMode) { await handleSave(); setIsEditMode(false); }
                  else { setIsEditMode(true); setEditingSection(null); setEditedProperty(property); }
                }}>
                  {isEditMode ? 'Done' : <><FaEdit /> Edit</>}
                </button>
              )}
            </div>
          </div>

          {/* Tab bar */}
          <div className="pd-tab-bar">
            {tabs.map(t => (
              <button key={t.key} className={`pd-tab-btn ${activeTab === t.key ? 'pd-tab-active' : ''}`} onClick={() => { setActiveTab(t.key); setEditingSection(null); setEditedProperty(property); }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'overview'    && renderOverview()}
          {activeTab === 'activity'    && renderActivity()}
          {activeTab === 'workorders'  && renderWorkOrders()}
          {activeTab === 'financial'   && renderFinancial()}
          {activeTab === 'info'        && renderInfo()}

          {/* Modals */}
          {renderFilterDrawer()}
          {renderAddIncomeModal()}
          {renderAddExpenseModal()}
          {renderPushFeeModal()}
          {renderAddWOModal()}
          {renderWODrawer()}
          {renderOthersModal()}
          {renderAddOccModal()}
          {renderUtilModal()}
          {renderAssetModal()}
          {renderDocModal()}
          {renderPastTenantModal()}
        </div>
      </div>
      {confirmDialog && (
        <ConfirmModal
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          variant={confirmDialog.variant || 'danger'}
          onConfirm={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

export default PropertyDetails;
